import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { UpProjetsCell } from '@/components/UpProjetsCell'
import { UpSpotBadge } from '@/components/UpSpotBadge'
import { UpSpotPanel } from '@/components/UpSpotPanel'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import CompanyAutocomplete, { CompanySuggestion } from '@/components/CompanyAutocomplete'
import { computeFrenchVat, formatFrenchVat } from '@/utils/vat-fr'
import { compareByLastName } from '@/utils/name-format'
import { useSettings } from '@/hooks/use-settings'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { 
  Search, 
  Filter,
  Plus,
  Settings,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  ExternalLink,
  Building2,
  MapPin,
  X,
  Users,
  Mail,
  Phone,
  Calendar,
  SlidersHorizontal,
  FolderKanban,
  FileCheck,
  Contact as ContactIcon,
  UserPlus,
  GitMerge,
} from 'lucide-react'
import {
  apiService,
  Company,
  CompanyCreateInputWithCustomFields,
  CompanyCustomValueInput,
  CompanyCustomField,
  CompanyCustomFieldOption,
  CustomField,
  CustomFieldOption,
} from '@/services/api'
import { useToast } from '@/hooks/use-toast'
import ChoixSuppressionUpSpot, { suppressionPossible, type ChoixCompteUpSpot } from '@/components/ChoixSuppressionUpSpot'
import { useAuthStore } from '@/store/authStore'
import FusionDialog from '@/components/FusionDialog'
import AlerteDoublon from '@/components/AlerteDoublon'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

/* Le nom du champ qui porte le suivi UP-PROJETS, et les valeurs de ses deux
   options. Écrits ici ET côté serveur (utils/database.ts) : c'est le contrat
   entre UP-CRM et UP-PROJETS, il ne doit pas être deviné. */
const CHAMP_SUIVI = 'Suivi dans UP-PROJETS'
const SUIVI_DOCUMENTAIRE = 'documentaire'
const SUIVI_ACTIONS = 'actions'

export default function Companies() {
  const { toast } = useToast()
  const { settings, loading: settingsLoading } = useSettings()

  // États pour la gestion des données
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // États pour la recherche et pagination
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  
  // Utiliser les paramètres pour itemsPerPage
  const itemsPerPage = settings.elementsPerPageCompanies
  
  // États pour le tri
  const [sortField, setSortField] = useState<string>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  
  // État pour les filtres
  const [showFilters, setShowFilters] = useState(false)
  const [advancedFilters, setAdvancedFilters] = useState<{
    field: string
    operator: string
    value: string
  }[]>([])
  const [filterLogicOperator, setFilterLogicOperator] = useState<'AND' | 'OR'>('AND')

  // États pour les filtres sauvegardés
  const [savedFilters, setSavedFilters] = useState<Array<{
    id: string
    name: string
    filters: { field: string; operator: string; value: string }[]
    logicOperator: 'AND' | 'OR'
    createdAt: string
  }>>([])
  const [isFilterSaveModalOpen, setIsFilterSaveModalOpen] = useState(false)
  const [filterSaveName, setFilterSaveName] = useState('')

  // États pour les champs de filtres
  const [availableFilterFields, setAvailableFilterFields] = useState<Array<{
    value: string
    label: string
    type: 'standard' | 'custom'
    fieldType?: 'text' | 'number' | 'date' | 'email' | 'url' | 'radio' | 'checkbox' | 'select'
    options?: Array<{ id: number; label: string; value?: string }>
  }>>([])
  
  // Champs standards pour les entreprises
  const standardFields = [
    { value: 'name', label: 'Nom', type: 'standard' as const, fieldType: 'text' as const },
    { value: 'website', label: 'Site web', type: 'standard' as const, fieldType: 'url' as const },
    { value: 'street_address', label: 'Adresse', type: 'standard' as const, fieldType: 'text' as const },
    { value: 'postal_code', label: 'Code postal', type: 'standard' as const, fieldType: 'text' as const },
    { value: 'city', label: 'Ville', type: 'standard' as const, fieldType: 'text' as const }
  ]

  // Opérateurs de filtre selon le type de champ
  const getFilterOperators = (fieldType?: string) => {
    switch (fieldType) {
      case 'number':
        return [
          { value: 'equals', label: 'Est égal à' },
          { value: 'greater_than', label: 'Supérieur à' },
          { value: 'less_than', label: 'Inférieur à' },
          { value: 'greater_equal', label: 'Supérieur ou égal à' },
          { value: 'less_equal', label: 'Inférieur ou égal à' },
          { value: 'is_empty', label: 'Est vide' },
          { value: 'is_not_empty', label: "N'est pas vide" }
        ]
      case 'date':
        return [
          { value: 'equals', label: 'Est égal à' },
          { value: 'before', label: 'Avant le' },
          { value: 'after', label: 'Après le' },
          { value: 'between', label: 'Entre' },
          { value: 'is_empty', label: 'Est vide' },
          { value: 'is_not_empty', label: "N'est pas vide" }
        ]
      case 'radio':
      case 'checkbox':
      case 'select':
        return [
          { value: 'equals', label: 'Est égal à' },
          { value: 'contains', label: 'Contient' },
          { value: 'is_empty', label: 'Est vide' },
          { value: 'is_not_empty', label: "N'est pas vide" }
        ]
      case 'text':
      case 'email':
      case 'url':
      default:
        return [
          { value: 'equals', label: 'Est égal à' },
          { value: 'contains', label: 'Contient' },
          { value: 'starts_with', label: 'Commence par' },
          { value: 'ends_with', label: 'Se termine par' },
          { value: 'is_empty', label: 'Est vide' },
          { value: 'is_not_empty', label: "N'est pas vide" }
        ]
    }
  }

  // États pour le modal de création d'entreprise
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createForm, setCreateForm] = useState({
    name: '',
    website: '',
    street_address: '',
    address_complement: '',
    postal_code: '',
    city: ''
  })
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({})

  // États pour le mode édition d'entreprise
  const [isEditMode, setIsEditMode] = useState(false)
  const [companyToEdit, setCompanyToEdit] = useState<(Company & { custom_field_values?: Record<number, string> }) | null>(null)

  // États pour la modal de confirmation de suppression
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // États pour les champs personnalisés des entreprises
  const [customFields, setCustomFields] = useState<(CompanyCustomField & { usage_count: number, options?: CompanyCustomFieldOption[] })[]>([])
  /* Ce que le suivi UP-PROJETS valait à l'ouverture de la fiche : la référence
     du verrou. Vide à la création — rien n'est encore engagé. */
  const [suiviEnregistre, setSuiviEnregistre] = useState('')
  const [customFieldValues, setCustomFieldValues] = useState<Record<number, string>>({})
  const [loadingCustomFields, setLoadingCustomFields] = useState(false)

  // États pour la gestion des colonnes visibles
  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false)
  const [availableColumns, setAvailableColumns] = useState<Array<{
    id: string
    label: string
    type: 'standard' | 'custom'
    visible: boolean
  }>>([])
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])

  // États pour la modal de détails de l'entreprise
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  /* La fusion de deux entreprises en double (28/10/2026) : la fiche de
     départ, et l'autre quand on la connaît déjà. */
  const [fusion, setFusion] = useState<{ ficheId: number; autreId?: number } | null>(null)
  const [selectedCompany, setSelectedCompany] = useState<(Company & { 
    custom_field_values?: Record<number, string>
    custom_fields?: Array<{
      field: CompanyCustomField & { options?: CompanyCustomFieldOption[] }
      value?: string
    }>
  }) | null>(null)
  const [loadingCompanyDetails, setLoadingCompanyDetails] = useState(false)

  // États pour les contacts de l'entreprise
  const [companyContacts, setCompanyContacts] = useState<Array<{
    id: number
    first_name: string
    last_name: string
    email?: string
    phone?: string
    company_id?: number
    created_at: string
    updated_at: string
    company_name?: string
    upspot_compte_id?: number | null
    upspot_role?: string | null
    upspot_statut?: 'actif' | 'desactive' | null
    upspot_invitation_en_attente?: number | boolean | null
  }>>([])
  const [loadingCompanyContacts, setLoadingCompanyContacts] = useState(false)

  // États pour la fiche d'un contact ouverte par-dessus la modal entreprise
  const [viewedContact, setViewedContact] = useState<any | null>(null)
  const [isContactDetailOpen, setIsContactDetailOpen] = useState(false)
  const [loadingViewedContact, setLoadingViewedContact] = useState(false)

  // Contact à retirer depuis la fiche entreprise (confirmation)
  const [contactToRemove, setContactToRemove] = useState<{ id: number; first_name: string; last_name: string; upspot_compte_id?: number | null } | null>(null)
  const [isRemovingContact, setIsRemovingContact] = useState(false)
  // Sort du compte UP-SPOT porté par la fiche à supprimer (01/10/2026).
  const [choixUpSpot, setChoixUpSpot] = useState<ChoixCompteUpSpot>('desactiver')
  const [suppressionConfirmee, setSuppressionConfirmee] = useState(false)
  const estAdminCrm = Boolean(useAuthStore((etat) => etat.user?.is_admin))

  // Ajout d'un nouveau contact directement depuis la fiche entreprise
  const [isAddContactOpen, setIsAddContactOpen] = useState(false)
  const [isCreatingContact, setIsCreatingContact] = useState(false)
  const [newContactForm, setNewContactForm] = useState({ first_name: '', last_name: '', email: '', phone: '' })
  const [contactCustomFields, setContactCustomFields] = useState<(CustomField & { options?: CustomFieldOption[] })[]>([])
  const [newContactCustomValues, setNewContactCustomValues] = useState<Record<number, string>>({})

  const openAddContactToCompany = async () => {
    setNewContactForm({ first_name: '', last_name: '', email: '', phone: '' })
    setNewContactCustomValues({})
    setIsAddContactOpen(true)
    try {
      const res = await apiService.getContactCustomFields()
      if (res.success && res.data) setContactCustomFields(res.data)
    } catch (e) {
      console.error('Erreur chargement champs perso contact:', e)
    }
  }

  // Renderer simplifié pour les champs perso du contact (formulaire d'ajout depuis fiche entreprise)
  const renderNewContactCustomField = (field: CustomField & { options?: CustomFieldOption[] }) => {
    const value = newContactCustomValues[field.id] || ''
    const setValue = (v: string) => setNewContactCustomValues({ ...newContactCustomValues, [field.id]: v })

    if (field.type === 'radio') {
      return (
        <div className="inline-flex p-0.5 rounded-lg bg-gray-100 text-sm font-medium">
          {field.options?.map((o) => {
            const ov = o.value || o.label
            const sel = value === ov
            const lower = o.label.trim().toLowerCase()
            const cls = lower === 'oui'
              ? 'bg-emerald-600 text-white'
              : lower === 'non'
                ? 'bg-rose-500 text-white'
                : 'bg-blue-600 text-white'
            return (
              <button key={o.id} type="button" onClick={() => setValue(ov)} className={`px-3 py-1 rounded-md transition-colors ${sel ? cls : 'text-gray-500 hover:text-gray-700'}`}>
                {o.label}
              </button>
            )
          })}
        </div>
      )
    }
    if (field.type === 'select') {
      return (
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger>
            <SelectValue placeholder="Sélectionner…" />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map((o) => (
              <SelectItem key={o.id} value={o.value || o.label}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }
    const inputType = field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'date' ? 'date' : field.type === 'url' ? 'url' : 'text'
    return (
      <Input
        type={inputType}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={`Entrez ${field.name.toLowerCase()}`}
      />
    )
  }

  const submitAddContact = async () => {
    if (!selectedCompany) return
    if (!newContactForm.first_name.trim() || !newContactForm.last_name.trim() || !newContactForm.email.trim()) {
      toast({ variant: 'destructive', title: 'Champs requis', description: 'Prénom, nom et email sont obligatoires.' })
      return
    }
    setIsCreatingContact(true)
    try {
      const payload: any = {
        first_name: newContactForm.first_name,
        last_name: newContactForm.last_name,
        email: newContactForm.email,
        phone: newContactForm.phone || undefined,
        company_id: selectedCompany.id,
        custom_fields: Object.entries(newContactCustomValues)
          .filter(([, v]) => v !== '' && v != null)
          .map(([k, v]) => ({ contacts_custom_field_id: parseInt(k, 10), value: v })),
      }
      const res = await apiService.createContact(payload)
      if (res.success) {
        toast({ title: '✅ Contact créé', variant: 'success' })
        if (res.avertissementUpRegistre) {
          toast({ variant: 'destructive', title: 'Registre des visiteurs non mis à jour', description: res.avertissementUpRegistre })
        }
        setIsAddContactOpen(false)
        await loadCompanyContacts(selectedCompany.id)
      } else {
        toast({ variant: 'destructive', title: 'Erreur', description: res.error || 'Création impossible.' })
      }
    } finally {
      setIsCreatingContact(false)
    }
  }

  // Supprimer un contact associé directement depuis la fiche entreprise
  const confirmRemoveContact = async () => {
    if (!contactToRemove || !selectedCompany) return
    setIsRemovingContact(true)
    try {
      const toutSupprimer = Boolean(contactToRemove.upspot_compte_id) && choixUpSpot === 'supprimer'
      const res = await apiService.deleteContact(
        contactToRemove.id,
        toutSupprimer ? { compteUpSpot: 'supprimer' } : {},
      )
      if (res.success) {
        toast({
          title: '✅ Contact mis à la corbeille',
          description: toutSupprimer
            ? 'Son compte UP-SPOT et ses réservations, eux, sont supprimés définitivement.'
            : contactToRemove.upspot_compte_id
              ? 'Son compte UP-SPOT est désactivé.'
              : 'Un administrateur peut le restaurer depuis la corbeille.',
          variant: 'success',
        })
        setContactToRemove(null)
        await loadCompanyContacts(selectedCompany.id)
      } else {
        // Le serveur dit pourquoi (fiche porteuse d'un compte UP-SPOT, par exemple) : on le montre.
        toast({ variant: 'destructive', title: 'Suppression impossible', description: res.error || 'Le contact n\'a pas pu être supprimé.' })
      }
    } finally {
      setIsRemovingContact(false)
    }
  }

  // Ouvrir la fiche d'un contact (modal empilée sur la modal entreprise)
  const handleViewCompanyContact = async (contactId: number) => {
    setViewedContact(null)
    setIsContactDetailOpen(true)
    setLoadingViewedContact(true)
    try {
      const response = await apiService.getContact(contactId)
      if (response.success && response.data) {
        setViewedContact(response.data)
      }
    } catch (error) {
      console.error('Erreur lors du chargement du contact:', error)
    } finally {
      setLoadingViewedContact(false)
    }
  }

  // Colonnes standards par défaut pour les entreprises
  const defaultColumns = [
    { id: 'name', label: 'Nom', type: 'standard' as const },
    { id: 'website', label: 'Site web', type: 'standard' as const },
    { id: 'street_address', label: 'Adresse', type: 'standard' as const },
    { id: 'postal_code', label: 'Code postal', type: 'standard' as const },
    { id: 'city', label: 'Ville', type: 'standard' as const }
  ]

  // Fonction pour formater le SIRET
  const formatSiret = (value: string): string => {
    // Supprimer tous les caractères non numériques
    const numbers = value.replace(/\D/g, '')
    
    // Limiter à 14 chiffres maximum
    const limitedNumbers = numbers.slice(0, 14)
    
    // Formater avec des espaces : XXX XXX XXX XXXXX
    if (limitedNumbers.length <= 3) {
      return limitedNumbers
    } else if (limitedNumbers.length <= 6) {
      return `${limitedNumbers.slice(0, 3)} ${limitedNumbers.slice(3)}`
    } else if (limitedNumbers.length <= 9) {
      return `${limitedNumbers.slice(0, 3)} ${limitedNumbers.slice(3, 6)} ${limitedNumbers.slice(6)}`
    } else {
      return `${limitedNumbers.slice(0, 3)} ${limitedNumbers.slice(3, 6)} ${limitedNumbers.slice(6, 9)} ${limitedNumbers.slice(9)}`
    }
  }

  // Fonction pour valider le SIRET
  const validateSiret = (value: string): boolean => {
    if (!value) return true // Optionnel si vide
    const numbers = value.replace(/\D/g, '')
    return numbers.length === 14
  }

  // Fonctions pour gérer les filtres avancés
  const addAdvancedFilter = () => {
    const defaultField = availableFilterFields.length > 0 ? availableFilterFields[0].value : 'name'
    setAdvancedFilters(prev => [...prev, { field: defaultField, operator: 'contains', value: '' }])
  }

  const updateAdvancedFilter = (index: number, field: 'field' | 'operator' | 'value', value: string) => {
    setAdvancedFilters(prev => prev.map((filter, i) => {
      if (i === index) {
        const updatedFilter = { ...filter, [field]: value }
        
        // Si on change le champ, réinitialiser l'opérateur et la valeur
        if (field === 'field') {
          const selectedField = availableFilterFields.find(f => f.value === value)
          const defaultOperators = getFilterOperators(selectedField?.fieldType)
          updatedFilter.operator = defaultOperators[0]?.value || 'equals'
          updatedFilter.value = ''
        }
        
        // Si on change l'opérateur vers "is_empty" ou "is_not_empty", vider la valeur
        if (field === 'operator' && ['is_empty', 'is_not_empty'].includes(value)) {
          updatedFilter.value = ''
        }
        
        return updatedFilter
      }
      return filter
    }))
  }

  const removeAdvancedFilter = (index: number) => {
    setAdvancedFilters(prev => prev.filter((_, i) => i !== index))
  }

  const resetAdvancedFilters = () => {
    setAdvancedFilters([])
  }

  // Fonction pour rendre le champ de valeur adaptatif selon le type de champ
  const renderValueField = (filter: { field: string; operator: string; value: string }, index: number) => {
    // Si l'opérateur ne nécessite pas de valeur
    if (['is_empty', 'is_not_empty'].includes(filter.operator)) {
      return (
        <div className="h-10 flex items-center px-3 py-2 border border-gray-200 bg-gray-50 rounded-md">
          <span className="text-sm text-gray-500">Aucune valeur requise</span>
        </div>
      )
    }

    // Trouver le champ sélectionné pour connaître son type
    const selectedField = availableFilterFields.find(f => f.value === filter.field)
    const fieldType = selectedField?.fieldType

    switch (fieldType) {
      case 'number':
        return (
          <Input
            type="number"
            placeholder="Valeur numérique"
            value={filter.value}
            onChange={(e) => updateAdvancedFilter(index, 'value', e.target.value)}
          />
        )

      case 'date':
        if (filter.operator === 'between') {
          const [startDate, endDate] = filter.value.split('|')
          return (
            <div className="flex items-center space-x-2">
              <Input
                type="date"
                placeholder="Date de début"
                value={startDate || ''}
                onChange={(e) => {
                  const newValue = `${e.target.value}|${endDate || ''}`
                  updateAdvancedFilter(index, 'value', newValue)
                }}
                className="flex-1"
              />
              <span className="text-sm text-gray-500">à</span>
              <Input
                type="date"
                placeholder="Date de fin"
                value={endDate || ''}
                onChange={(e) => {
                  const newValue = `${startDate || ''}|${e.target.value}`
                  updateAdvancedFilter(index, 'value', newValue)
                }}
                className="flex-1"
              />
            </div>
          )
        } else {
          return (
            <Input
              type="date"
              placeholder="Date"
              value={filter.value}
              onChange={(e) => updateAdvancedFilter(index, 'value', e.target.value)}
            />
          )
        }

      case 'radio':
      case 'select':
        return (
          <Select 
            value={filter.value} 
            onValueChange={(value) => updateAdvancedFilter(index, 'value', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner une option..." />
            </SelectTrigger>
            <SelectContent>
              {selectedField?.options?.map((option) => (
                <SelectItem key={option.id} value={option.value || option.label}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      case 'checkbox':
        return (
          <Select 
            value={filter.value} 
            onValueChange={(value) => updateAdvancedFilter(index, 'value', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner une ou plusieurs options..." />
            </SelectTrigger>
            <SelectContent>
              {selectedField?.options?.map((option) => (
                <SelectItem key={option.id} value={option.value || option.label}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      case 'email':
        return (
          <Input
            type="email"
            placeholder="Adresse email"
            value={filter.value}
            onChange={(e) => updateAdvancedFilter(index, 'value', e.target.value)}
          />
        )

      case 'url':
        return (
          <Input
            type="url"
            placeholder="URL (https://...)"
            value={filter.value}
            onChange={(e) => updateAdvancedFilter(index, 'value', e.target.value)}
          />
        )

      case 'text':
      default:
        return (
          <Input
            placeholder="Valeur"
            value={filter.value}
            onChange={(e) => updateAdvancedFilter(index, 'value', e.target.value)}
          />
        )
    }
  }

  // Fonction pour gérer le tri des colonnes
  const handleSort = (columnId: string) => {
    if (sortField === columnId) {
      // Si on clique sur la même colonne, inverser l'ordre
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // Si on clique sur une nouvelle colonne, trier par ordre croissant
      setSortField(columnId)
      setSortOrder('asc')
    }
    // Remettre à la première page lors d'un changement de tri
    setCurrentPage(1)
  }

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = (columnId: string) => {
    if (sortField !== columnId) {
      return '↕' // Icône neutre
    }
    return sortOrder === 'asc' ? '↑' : '↓'
  }

  // Fonctions pour gérer les filtres sauvegardés
  const loadSavedFilters = () => {
    try {
      const saved = localStorage.getItem('companies_saved_filters')
      if (saved) {
        setSavedFilters(JSON.parse(saved))
      }
    } catch (error) {
      console.error('Erreur lors du chargement des filtres sauvegardés:', error)
    }
  }

  const saveCurrentFilters = () => {
    if (!filterSaveName.trim()) return

    const newFilter = {
      id: Date.now().toString(),
      name: filterSaveName.trim(),
      filters: advancedFilters,
      logicOperator: filterLogicOperator,
      createdAt: new Date().toISOString()
    }

    const updatedFilters = [...savedFilters, newFilter]
    setSavedFilters(updatedFilters)
    localStorage.setItem('companies_saved_filters', JSON.stringify(updatedFilters))
    
    setFilterSaveName('')
    setIsFilterSaveModalOpen(false)
    
    toast({
      variant: "success",
      title: "✅ Filtre sauvegardé !",
      description: `Le filtre "${newFilter.name}" a été sauvegardé avec succès.`
    })
  }

  // Fonctions pour la gestion des colonnes visibles
  const loadAvailableColumns = async () => {
    try {
      // Charger les champs personnalisés
      const response = await apiService.getCompanyCustomFieldsWithUsage()
      
      let customColumns: Array<{id: string, label: string, type: 'custom'}> = []
      
      if (response.success && response.data) {
        customColumns = response.data
          /* « Suivi dans UP-PROJETS » n'est plus proposé en colonne : la colonne
             UP-PROJETS, ajoutée le 27/08/2026, dit la même chose en clair —
             « Espace documentaire » plutôt que la valeur brute « documentaire »
             — et porte en plus le bouton pour écrire. Deux colonnes pour la
             même information, c'est une de trop. */
          .filter(field => field.name.trim().toLowerCase() !== CHAMP_SUIVI.toLowerCase())
          .map(field => ({
            id: `custom_${field.id}`,
            label: field.name,
            type: 'custom' as const
          }))
      }
      
      // Combiner colonnes standard et personnalisées
      const allColumns = [...defaultColumns, ...customColumns]
      
      // Charger les préférences utilisateur depuis localStorage
      const savedColumns = localStorage.getItem('companies_visible_columns')
      let visibleColumnIds: string[] = []
      
      if (savedColumns) {
        try {
          visibleColumnIds = JSON.parse(savedColumns)
        } catch {
          // En cas d'erreur, utiliser les colonnes par défaut
          visibleColumnIds = defaultColumns.slice(0, 4).map(col => col.id) // 4 colonnes pour les entreprises
        }
      } else {
        // Première visite, utiliser les 4 premières colonnes par défaut
        visibleColumnIds = defaultColumns.slice(0, 4).map(col => col.id)
      }
      
      /* Les préférences enregistrées peuvent encore porter la colonne du champ
         de suivi : elle a été choisie avant qu'on la retire. On l'écarte de ce
         qui est affiché, sinon elle resterait en double pour ceux qui l'avaient
         sélectionnée — une préférence figée n'a pas connaissance de ce qui a
         changé depuis. */
      const idsConnus = new Set(allColumns.map(col => col.id))
      visibleColumnIds = visibleColumnIds.filter(id => idsConnus.has(id))

      // Limiter à 5 colonnes maximum
      if (visibleColumnIds.length > 5) {
        visibleColumnIds = visibleColumnIds.slice(0, 5)
      }
      
      // Marquer les colonnes comme visibles ou non
      const columnsWithVisibility = allColumns.map(col => ({
        ...col,
        visible: visibleColumnIds.includes(col.id)
      }))
      
      setAvailableColumns(columnsWithVisibility)
      setSelectedColumns(visibleColumnIds)
      
    } catch (err) {
      console.error('Erreur lors du chargement des colonnes:', err)
      // En cas d'erreur, utiliser seulement les colonnes standards
      const standardWithVisibility = defaultColumns.map((col, index) => ({
        ...col,
        visible: index < 4 // 4 colonnes par défaut pour les entreprises
      }))
      setAvailableColumns(standardWithVisibility)
      setSelectedColumns(defaultColumns.slice(0, 4).map(col => col.id))
    }
  }

  const toggleColumn = (columnId: string) => {
    const newSelectedColumns = selectedColumns.includes(columnId)
      ? selectedColumns.filter(id => id !== columnId)
      : [...selectedColumns, columnId]
    
    // Limiter à 5 colonnes maximum
    if (newSelectedColumns.length <= 5) {
      setSelectedColumns(newSelectedColumns)
      
      // Mettre à jour l'état des colonnes disponibles
      setAvailableColumns(prev => prev.map(col => ({
        ...col,
        visible: newSelectedColumns.includes(col.id)
      })))
      
      // Sauvegarder dans localStorage
      localStorage.setItem('companies_visible_columns', JSON.stringify(newSelectedColumns))
    }
  }

  const selectAllColumns = () => {
    const allColumnIds = availableColumns.map(col => col.id)
    const newSelectedColumns = allColumnIds.slice(0, 5) // Limiter à 5
    
    setSelectedColumns(newSelectedColumns)
    setAvailableColumns(prev => prev.map((col, index) => ({
      ...col,
      visible: index < 5
    })))
    
    localStorage.setItem('companies_visible_columns', JSON.stringify(newSelectedColumns))
  }

  const deselectAllColumns = () => {
    setSelectedColumns([])
    setAvailableColumns(prev => prev.map(col => ({
      ...col,
      visible: false
    })))
    
    localStorage.setItem('companies_visible_columns', JSON.stringify([]))
  }

  // Charger les champs disponibles pour les filtres (standards + personnalisés)
  const loadAvailableFilterFields = async () => {
    try {
      const response = await apiService.getCompanyCustomFieldsWithUsage()
      
      let customFields: Array<{
        value: string
        label: string
        type: 'custom'
        fieldType: 'text' | 'number' | 'date' | 'email' | 'url' | 'radio' | 'checkbox' | 'select'
        options?: Array<{ id: number; label: string; value?: string }>
      }> = []
      
      if (response.success && response.data) {
        customFields = response.data.map(field => ({
          value: `custom_${field.id}`,
          label: field.name,
          type: 'custom' as const,
          fieldType: field.type,
          options: field.options || []
        }))
      }
      
      // Combiner champs standards et personnalisés
      setAvailableFilterFields([...standardFields, ...customFields])
    } catch (err) {
      console.error('Erreur lors du chargement des champs de filtres:', err)
      // En cas d'erreur, utiliser seulement les champs standards
      setAvailableFilterFields(standardFields)
    }
  }

  // Charger les entreprises depuis l'API
  const loadCompanies = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Préparer les filtres avancés pour l'API
      const filters = advancedFilters.filter(f => {
        // Pour les opérateurs "is_empty" et "is_not_empty", pas besoin de valeur
        if (['is_empty', 'is_not_empty'].includes(f.operator)) {
          return f.field && f.operator
        }
        // Pour les autres opérateurs, vérifier que la valeur n'est pas vide
        return f.field && f.operator && f.value.trim() !== ''
      })
      
      const response = await apiService.getCompanies({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        sort: sortField,
        order: sortOrder,
        filters: filters.length > 0 ? {
          filters: filters,
          logicOperator: filterLogicOperator
        } : undefined
      })

      if (response.success && response.data) {
        setCompanies(response.data.data)
        setTotalItems(response.data.total)
        setTotalPages(response.data.totalPages)
      } else {
        setError(response.error || 'Erreur lors du chargement des entreprises')
      }
    } catch (err) {
      setError('Erreur de connexion')
      console.error('Erreur lors du chargement des entreprises:', err)
    } finally {
      setLoading(false)
    }
  }

  // Charger les champs personnalisés pour les entreprises
  const loadCustomFields = async (preserveValues = false) => {
    try {
      setLoadingCustomFields(true)
      const response = await apiService.getCompanyCustomFieldsWithUsage()
      
      if (response.success && response.data) {
        setCustomFields(response.data)
        // Réinitialiser les valeurs des champs personnalisés seulement si pas en mode préservation
        if (!preserveValues) {
          setCustomFieldValues({})
        }
      }
    } catch (err) {
      console.error('Erreur lors du chargement des champs personnalisés des entreprises:', err)
    } finally {
      setLoadingCustomFields(false)
    }
  }

  // Gérer les changements des champs personnalisés
  const handleCustomFieldChange = (fieldId: number, value: string) => {
    // Trouver le champ correspondant pour vérifier s'il s'agit du SIRET
    const field = customFields.find(f => f.id === fieldId)
    let formattedValue = value
    
    // Si c'est un champ SIRET, appliquer le formatage automatique
    if (field && field.name.toUpperCase().includes('SIRET')) {
      formattedValue = formatSiret(value)
    }
    
    setCustomFieldValues(prev => ({
      ...prev,
      [fieldId]: formattedValue
    }))
  }

  // Gérer la création/modification d'une entreprise
  const handleCreateCompany = async () => {
    try {
      setIsCreating(true)
      setCreateErrors({})

      // Validation
      const errors: Record<string, string> = {}
      if (!createForm.name.trim()) {
        errors.name = 'Le nom de l\'entreprise est requis'
      }

      // Validation des champs personnalisés SIRET
      for (const [fieldId, value] of Object.entries(customFieldValues)) {
        if (value && value.trim() !== '') {
          const field = customFields.find(f => f.id === parseInt(fieldId))
          if (field && field.name.toUpperCase().includes('SIRET')) {
            if (!validateSiret(value)) {
              toast({
                variant: "destructive",
                title: "Erreur de validation",
                description: "Le SIRET doit contenir exactement 14 chiffres"
              })
              return
            }
          }
        }
      }

      if (Object.keys(errors).length > 0) {
        setCreateErrors(errors)
        return
      }

      // Préparer les données avec les champs personnalisés
      const customFieldsData: CompanyCustomValueInput[] = Object.entries(customFieldValues)
        .filter(([_fieldId, value]) => value && value.trim() !== '')
        .map(([fieldId, value]) => ({
          company_custom_field_id: parseInt(fieldId),
          value: value.trim()
        }))

      const companyData: CompanyCreateInputWithCustomFields = {
        name: createForm.name.trim(),
        website: createForm.website.trim() || undefined,
        street_address: createForm.street_address.trim() || undefined,
        address_complement: createForm.address_complement.trim() || undefined,
        postal_code: createForm.postal_code.trim() || undefined,
        city: createForm.city.trim() || undefined,
        custom_fields: customFieldsData.length > 0 ? customFieldsData : undefined
      }

      let response

      if (isEditMode && companyToEdit) {
        // Mode édition : mettre à jour l'entreprise existante
        response = await apiService.updateCompany(companyToEdit.id, companyData)
      } else {
        // Mode création : créer une nouvelle entreprise
        response = await apiService.createCompany(companyData)
      }

      if (response.success) {
        // Fermer la modal et réinitialiser
        handleCloseModal()
        
        // Recharger la liste des entreprises
        await loadCompanies()
        
        // Afficher le toast de succès
        toast({
          variant: "success",
          title: isEditMode ? "✅ Entreprise modifiée !" : "✅ Entreprise créée !",
          description: isEditMode 
            ? `${companyData.name} a été modifiée avec succès`
            : `${companyData.name} a été ajoutée avec succès`
        })
      } else {
        toast({
          variant: "destructive",
          title: isEditMode ? "Erreur de modification" : "Erreur de création",
          description: response.error || "Une erreur s'est produite lors de l'opération"
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur de connexion",
        description: isEditMode 
          ? "Impossible de modifier l'entreprise. Vérifiez votre connexion."
          : "Impossible de créer l'entreprise. Vérifiez votre connexion."
      })
    } finally {
      setIsCreating(false)
    }
  }

  // Identification dynamique des champs perso "légaux" (annuaire officiel)
  const findSiretField = () =>
    customFields.find((f) => f.name.trim().toLowerCase() === 'siret')
  // Champ TVA : tolère "TVA", "TVA intracommunautaire", "N° TVA", "VAT"…
  const findVatField = () =>
    customFields.find((f) => /\btva\b|intracommunautaire|\bvat\b/i.test(f.name))
  // Vrai si le champ perso fait partie des identifiants légaux (SIRET / TVA)
  const isLegalField = (fieldId: number) =>
    fieldId === findSiretField()?.id || fieldId === findVatField()?.id

  /* Le champ « Suivi dans UP-PROJETS » (25/08/2026).

     Il est reconnu par son NOM exact, comme le SIRET l'est déjà, et rendu dans
     sa propre section : ce n'est pas une caractéristique de l'entreprise, c'est
     une décision d'organisation. Les VALEURS de ses options sont ce sur quoi
     les deux applications s'accordent — les libellés peuvent être reformulés
     sans rien casser. */
  const findSuiviField = () =>
    customFields.find((f) => f.name.trim().toLowerCase() === CHAMP_SUIVI.toLowerCase())
  const isSuiviField = (fieldId: number) => fieldId === findSuiviField()?.id

  /* Ce que le champ « Suivi dans UP-PROJETS » dit d'une entreprise de la liste.
     La valeur arrive tantôt dans `custom_fields` (fiche détaillée), tantôt dans
     `custom_field_values` (liste) : on lit les deux plutôt que de supposer. */
  const suiviDeLEntreprise = (company: Company): string[] => {
    const champ = findSuiviField()
    if (!champ) return []
    /* Le type `Company` ne décrit pas les champs personnalisés : ils arrivent
       en plus de l'entreprise, sous deux formes selon la route appelée. On les
       lit donc explicitement, plutôt que d'élargir un type partagé pour un
       besoin local. */
    const avecChamps = company as Company & {
      custom_fields?: Array<{ field?: { id: number }; value?: string }>
      custom_field_values?: Record<number, string>
    }
    const valeur = avecChamps.custom_fields?.find((c) => c.field?.id === champ.id)?.value
      ?? avecChamps.custom_field_values?.[champ.id]
      ?? ''
    return String(valeur).split(',').map((v) => v.trim()).filter(Boolean)
  }

  // Met en forme la valeur d'un champ perso pour l'affichage.
  // La Typologie est toujours affichée en MAJUSCULES.
  const displayCustomValue = (
    field: { name: string } | undefined,
    value: string,
  ) => {
    if (!value) return value
    if (field && field.name.trim().toLowerCase() === 'typologie') {
      return value.toUpperCase()
    }
    return value
  }

  // Auto-remplissage du formulaire depuis une suggestion API gouv
  const applyCompanySuggestion = (s: CompanySuggestion) => {
    setCreateForm((prev) => ({
      ...prev,
      name: s.name || prev.name,
      street_address: s.streetAddress || prev.street_address,
      postal_code: s.postalCode || prev.postal_code,
      city: s.city || prev.city,
    }))
    setCreateErrors((prev) => ({ ...prev, name: '' }))

    // Pré-remplir les identifiants légaux (SIRET + TVA) trouvés dynamiquement
    const updates: Record<number, string> = {}
    if (s.siret) {
      const siretField = findSiretField()
      if (siretField) updates[siretField.id] = s.siret
    }
    if (s.vatNumber) {
      const vatField = findVatField()
      // On stocke la TVA au format lisible "FR 42 753 108 900"
      if (vatField) updates[vatField.id] = formatFrenchVat(s.vatNumber)
    }
    if (Object.keys(updates).length > 0) {
      setCustomFieldValues((prev) => ({ ...prev, ...updates }))
    }
  }

  // Gérer les changements dans le formulaire de création
  const handleCreateFormChange = (field: string, value: string) => {
    setCreateForm(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Effacer l'erreur pour ce champ
    if (createErrors[field]) {
      setCreateErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  // Charger les données au montage du composant et quand les paramètres changent
  useEffect(() => {
    // Attendre que les settings soient chargés
    if (settingsLoading) return
    loadCompanies()
  }, [currentPage, searchTerm, advancedFilters, itemsPerPage, sortField, sortOrder, settingsLoading])

  // Charger les champs de filtres au montage
  useEffect(() => {
    loadAvailableFilterFields()
    loadAvailableColumns()
    loadSavedFilters()
    loadCustomFields()
  }, [])

  // Charger les champs personnalisés quand le modal s'ouvre
  useEffect(() => {
    if (isCreateModalOpen) {
      // En mode édition, préserver les valeurs déjà définies
      loadCustomFields(isEditMode)
    }
  }, [isCreateModalOpen, isEditMode])

  // Ouvrir automatiquement la fiche d'une entreprise ciblée via l'URL (?open=X)
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const openId = searchParams.get('open')
    if (!openId) return
    const id = parseInt(openId, 10)
    if (isNaN(id)) return
    ;(async () => {
      try {
        const response = await apiService.getCompany(id)
        if (response.success && response.data) {
          setSelectedCompany(response.data as any)
          setIsDetailModalOpen(true)
          await loadCompanyContacts(id)
        }
      } catch (e) {
        console.error('Erreur ouverture entreprise depuis URL:', e)
      } finally {
        // Nettoyer le paramètre pour éviter la réouverture au rafraîchissement
        searchParams.delete('open')
        setSearchParams(searchParams, { replace: true })
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Gérer les changements de page
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  // Gérer la recherche
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1) // Reset à la première page lors d'une recherche
  }

  // Ouvrir la modal de confirmation de suppression
  const handleDeleteCompany = async (company: Company) => {
    setCompanyToDelete(company)
    setIsDeleteModalOpen(true)
  }

  // Confirmer la suppression de l'entreprise
  const confirmDeleteCompany = async () => {
    if (!companyToDelete) return

    try {
      setIsDeleting(true)
      const response = await apiService.deleteCompany(companyToDelete.id)
        if (response.success) {
          await loadCompanies() // Recharger la liste
        setIsDeleteModalOpen(false)
        setCompanyToDelete(null)
          toast({
            variant: "success",
            title: "✅ Entreprise mise à la corbeille",
            description: "Un administrateur peut la restaurer depuis la corbeille, avec ses contacts."
          })
        } else {
          toast({
            variant: "destructive",
            title: "Erreur de suppression",
            description: response.error || "Impossible de supprimer l'entreprise"
          })
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Erreur de connexion",
          description: "Impossible de supprimer l'entreprise. Vérifiez votre connexion."
        })
    } finally {
      setIsDeleting(false)
    }
  }

  // Annuler la suppression
  const cancelDeleteCompany = () => {
    setIsDeleteModalOpen(false)
    setCompanyToDelete(null)
  }

  // Fonction pour rendre un champ personnalisé selon son type
  /* Le suivi UP-PROJETS, rendu à part.

     Trois règles, et elles ne sont pas décoratives :

     1. **Cocher le suivi des actions coche le documentaire.** Un chantier
        s'accompagne toujours de sa documentation ; l'inverse n'est pas vrai.
     2. **On ne décoche pas.** Cocher engage une création dans UP-PROJETS ; la
        case se libère quand l'espace correspondant y est retiré, et c'est
        UP-PROJETS qui la décoche. Laisser décocher ici ferait croire à une
        suppression qui n'aurait pas lieu.
     3. **Le verrou porte sur l'état ENREGISTRÉ**, pas sur l'état du formulaire :
        ce qu'on vient de cocher sans avoir enregistré reste décochable.
  */
  const renderSuiviUpProjets = (
    field: CompanyCustomField & { options?: CompanyCustomFieldOption[] },
  ) => {
    const courant = (customFieldValues[field.id] || '').split(',').filter(Boolean)
    const engage = suiviEnregistre.split(',').filter(Boolean)

    const basculer = (valeur: string, coche: boolean) => {
      let suivant = new Set(courant)
      if (coche) {
        suivant.add(valeur)
        // Le suivi des actions entraîne le documentaire, jamais l'inverse.
        if (valeur === SUIVI_ACTIONS) suivant.add(SUIVI_DOCUMENTAIRE)
      } else {
        suivant.delete(valeur)
        // Retirer le documentaire retire aussi les actions : elles en dépendent.
        if (valeur === SUIVI_DOCUMENTAIRE) suivant.delete(SUIVI_ACTIONS)
      }
      // L'ordre suit celui des options, pour que la valeur stockée soit stable.
      const ordonne = (field.options ?? [])
        .map((o) => o.value || o.label)
        .filter((v) => suivant.has(v))
      handleCustomFieldChange(field.id, ordonne.join(','))
    }

    return (
      <div className="space-y-2">
        {(field.options ?? []).map((option) => {
          const valeur = option.value || option.label
          const coche = courant.includes(valeur)
          const verrouille = engage.includes(valeur)
          return (
            <label
              key={option.id}
              className={`flex items-start gap-3 rounded-lg px-3 py-2.5 ring-1 transition-colors ${
                verrouille
                  ? 'bg-slate-50 ring-slate-200 cursor-default'
                  : coche
                    ? 'bg-blue-50 ring-blue-200 cursor-pointer'
                    : 'bg-white ring-gray-200 hover:bg-gray-50 cursor-pointer'
              }`}
            >
              <input
                type="checkbox"
                checked={coche}
                disabled={verrouille}
                onChange={(e) => basculer(valeur, e.target.checked)}
                className="mt-0.5 w-4 h-4 text-blue-600 disabled:opacity-60"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-800">{option.label}</span>
                <span className="block text-xs text-slate-500">
                  {valeur === SUIVI_ACTIONS
                    ? "Crée un projet dans UP-PROJETS, avec la saisie de temps et le suivi des actions. Entraîne l'espace documentaire."
                    : 'Crée un espace de notes et de comptes rendus dans UP-PROJETS.'}
                  {verrouille && ' Pour le retirer, supprimez l\'espace dans UP-PROJETS : la case se libérera.'}
                </span>
              </span>
            </label>
          )
        })}
      </div>
    )
  }

  const renderCustomField = (field: CompanyCustomField & { options?: CompanyCustomFieldOption[] }) => {
    const value = customFieldValues[field.id] || ''
    const isRequired = field.required === 1
    const isSiretField = field.name.toUpperCase().includes('SIRET')

    switch (field.type) {
      case 'text':
        // Traitement spécial pour le champ SIRET
        if (isSiretField) {
          const isValid = validateSiret(value)
          return (
            <div key={field.id} className="space-y-2">
              <div className="relative">
                <Input
                  value={value}
                  onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                  placeholder="123 456 789 01234"
                  required={isRequired}
                  className={`pr-10 ${
                    value && !isValid 
                      ? 'border-red-500 focus:border-red-500' 
                      : value && isValid 
                        ? 'border-green-500 focus:border-green-500' 
                        : ''
                  }`}
                />
                {value && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {isValid ? (
                      <span className="text-green-500 text-sm">✓</span>
                    ) : (
                      <span className="text-red-500 text-sm">✗</span>
                    )}
                  </div>
                )}
              </div>
              {value && !isValid && (
                <p className="text-red-500 text-xs">
                  Le SIRET doit contenir exactement 14 chiffres
                </p>
              )}
              {value && isValid && (
                <p className="text-green-500 text-xs">
                  Format valide
                </p>
              )}
            </div>
          )
        }
        
        // Champ texte normal
        return (
          <Input
            key={field.id}
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            placeholder={`Entrez ${field.name.toLowerCase()}`}
            required={isRequired}
          />
        )

      case 'number':
        return (
          <Input
            key={field.id}
            type="number"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            placeholder={`Entrez ${field.name.toLowerCase()}`}
            required={isRequired}
          />
        )

      case 'email':
        return (
          <Input
            key={field.id}
            type="email"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            placeholder={`email@exemple.com`}
            required={isRequired}
          />
        )

      case 'url':
        return (
          <Input
            key={field.id}
            type="url"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            placeholder={`https://exemple.com`}
            required={isRequired}
          />
        )

      case 'date':
        return (
          <Input
            key={field.id}
            type="date"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            required={isRequired}
          />
        )

      case 'select':
        return (
          <Select
            key={field.id}
            value={value}
            onValueChange={(selectedValue) => handleCustomFieldChange(field.id, selectedValue)}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Sélectionner ${field.name.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option.id} value={option.value || option.label}>
                  <div className="flex items-center space-x-2">
                    {field.color === 1 && option.color && (
                      <div
                        className="w-3 h-3 rounded-full border"
                        style={{ backgroundColor: option.color }}
                      />
                    )}
                    <span>{option.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      case 'radio':
        return (
          <div
            key={field.id}
            className="inline-flex p-0.5 rounded-lg bg-gray-100 text-sm font-medium"
          >
            {field.options?.map((option) => {
              const optionValue = option.value || option.label
              const selected = value === optionValue
              const lower = option.label.trim().toLowerCase()
              const selectedClasses =
                lower === 'oui'
                  ? 'bg-emerald-600 text-white'
                  : lower === 'non'
                    ? 'bg-rose-500 text-white'
                    : 'bg-blue-600 text-white'
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleCustomFieldChange(field.id, optionValue)}
                  className={`px-3 py-1 rounded-md transition-colors ${
                    selected
                      ? selectedClasses
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              )
            })}
          </div>
        )

      case 'checkbox':
        return (
          <div key={field.id} className="space-y-2">
            {field.options?.map((option) => {
              const selectedValues = value ? value.split(',') : []
              const isChecked = selectedValues.includes(option.value || option.label)
              
              return (
                <div key={option.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      const optionValue = option.value || option.label
                      let newValues = [...selectedValues]
                      
                      if (e.target.checked) {
                        newValues.push(optionValue)
                      } else {
                        newValues = newValues.filter(v => v !== optionValue)
                      }
                      
                      handleCustomFieldChange(field.id, newValues.join(','))
                    }}
                    className="w-4 h-4 text-blue-600"
                  />
                  {field.color === 1 && option.color && (
                    <div
                      className="w-3 h-3 rounded-full border"
                      style={{ backgroundColor: option.color }}
                    />
                  )}
                  <Label className="text-sm font-normal cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              )
            })}
          </div>
        )

      default:
        return (
          <Input
            key={field.id}
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            placeholder={`Entrez ${field.name.toLowerCase()}`}
            required={isRequired}
          />
        )
    }
  }

  // Formater la date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR')
  }

  // Fonction pour obtenir la valeur d'une colonne pour une entreprise
  const getColumnValue = (company: Company & { custom_field_values?: Record<number, string> }, columnId: string) => {
    if (columnId.startsWith('custom_')) {
      // Pour les champs personnalisés, extraire l'ID et chercher dans les valeurs
      const fieldId = parseInt(columnId.replace('custom_', ''))
      const customFieldValues = (company as any).custom_field_values || {}
      const value = customFieldValues[fieldId]
      // S'assurer que la valeur est une chaîne et retourner '-' si vide
      if (!value || typeof value !== 'string' || value.trim() === '') return '-'
      const field = customFields.find((f) => f.id === fieldId)
      return displayCustomValue(field, value)
    }
    
    switch (columnId) {
      case 'name':
        return company.name || '-'
      case 'website':
        return company.website || '-'
      case 'street_address':
        return company.street_address || '-'
      case 'postal_code':
        return company.postal_code || '-'
      case 'city':
        return company.city || '-'
      default:
        return '-'
    }
  }

  // Fonction pour décoder les entités HTML et s'assurer de l'affichage correct des caractères spéciaux
  const decodeHtmlEntities = (text: string): string => {
    if (!text || typeof text !== 'string') return text
    
    try {
      // Créer un élément temporaire pour décoder les entités HTML
      const textarea = document.createElement('textarea')
      textarea.innerHTML = text
      let decoded = textarea.value
      
      // Gérer les problèmes d'encodage UTF-8 courants
      const encodingFixes: Record<string, string> = {
        'Ã©': 'é',
        'Ã¨': 'è',
        'Ã ': 'à',
        'Ã¢': 'â',
        'Ã´': 'ô',
        'Ã®': 'î',
        'Ã¯': 'ï',
        'Ã¹': 'ù',
        'Ã»': 'û',
        'Ã§': 'ç',
        'Ã«': 'ë',
        'Ã¶': 'ö',
        'Ã¼': 'ü',
        'Ã¤': 'ä',
        'Ã±': 'ñ',
        'Ã': 'À',
        'Ã‰': 'É',
        'Ã‡': 'Ç',
        'Ãˆ': 'È',
        'ÃŠ': 'Ê',
        'Ã‹': 'Ë',
        'ÃŽ': 'Î',
        'Ã"': 'Ô',
        'Ã™': 'Ù',
        'Ã›': 'Û',
        "Ã'": 'Ñ'
      }
      
      // Appliquer les corrections d'encodage
      for (const [wrong, correct] of Object.entries(encodingFixes)) {
        decoded = decoded.replace(new RegExp(wrong, 'g'), correct)
      }
      
      return decoded
    } catch (error) {
      console.warn('Erreur lors du décodage des entités HTML:', error)
      return text
    }
  }

  // Fonction pour rendre une cellule de tableau
  const renderTableCell = (company: Company & { custom_field_values?: Record<number, string> }, columnId: string) => {
    const rawValue = getColumnValue(company, columnId)
    // Décoder les entités HTML pour s'assurer que les caractères spéciaux s'affichent correctement
    const value = typeof rawValue === 'string' ? decodeHtmlEntities(rawValue) : rawValue
    
    switch (columnId) {
      case 'name':
        return (
          <div className="flex items-center space-x-2">
            <Building2 className="w-4 h-4 text-gray-400" />
            <span className="font-medium">{value}</span>
          </div>
        )
      case 'website':
        return value !== '-' ? (
          <div className="flex items-center space-x-1">
            <ExternalLink className="w-3 h-3 text-gray-400" />
            <a 
              href={value.startsWith('http') ? value : `https://${value}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {value}
            </a>
          </div>
        ) : (
          <span className="text-gray-500">-</span>
        )
      case 'street_address':
        return value !== '-' ? (
          <div className="flex items-center space-x-1">
            <MapPin className="w-3 h-3 text-gray-400" />
            <span>{value}</span>
          </div>
        ) : (
          <span className="text-gray-500">-</span>
        )
      case 'postal_code':
      case 'city':
        return <span>{value}</span>
      default:
        return <span>{value}</span>
    }
  }

  // Fonction pour obtenir le label d'une colonne
  const getColumnLabel = (columnId: string) => {
    const column = availableColumns.find(col => col.id === columnId)
    return column ? decodeHtmlEntities(column.label) : columnId
  }

  // Fonction pour charger les contacts d'une entreprise
  const loadCompanyContacts = async (companyId: number) => {
    setLoadingCompanyContacts(true)
    // Réinitialiser immédiatement les contacts pour éviter d'afficher les anciens
    setCompanyContacts([])
    
    try {
      console.log('Chargement des contacts pour l\'entreprise ID:', companyId)
      
      const response = await apiService.getContacts({
        page: 1,
        limit: 50, // Limiter à 50 contacts pour éviter de surcharger
        filters: {
          filters: [
            {
              field: 'company_id',
              operator: 'equals',
              value: companyId.toString()
            }
          ],
          logicOperator: 'AND'
        }
      })
      
      console.log('Réponse API contacts:', response)
      
      if (response.success && response.data && response.data.data) {
        // Filtrer côté client aussi pour être sûr (double sécurité)
        const filteredContacts = response.data.data.filter(contact => 
          String(contact.company_id) === String(companyId)
        )
        console.log('Contacts filtrés pour l\'entreprise:', filteredContacts)
        // Les contacts d'une entreprise sont présentés par ordre alphabétique de nom
        setCompanyContacts([...filteredContacts].sort(compareByLastName))
      } else {
        console.log('Aucun contact trouvé ou réponse invalide')
        setCompanyContacts([])
      }
    } catch (error) {
      console.error('Erreur lors du chargement des contacts:', error)
      setCompanyContacts([])
    } finally {
      setLoadingCompanyContacts(false)
    }
  }

  // Fonction pour charger les détails complets d'une entreprise
  const loadCompanyDetails = async (companyId: number) => {
    setLoadingCompanyDetails(true)
    try {
      const response = await apiService.getCompany(companyId)
      if (response.success && response.data) {
        // Mettre à jour l'entreprise sélectionnée avec les détails complets
        setSelectedCompany(response.data)
        // Charger les contacts de l'entreprise
        await loadCompanyContacts(companyId)
      } else {
        toast({
          variant: "default",
          title: "❌ Erreur",
          description: "Impossible de charger les détails de l'entreprise."
        })
      }
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error)
      toast({
        variant: "default",
        title: "❌ Erreur",
        description: "Une erreur est survenue lors du chargement des détails."
      })
    } finally {
      setLoadingCompanyDetails(false)
    }
  }

  // Fonction pour ouvrir la modal de détails
  const handleViewCompany = async (company: Company & { custom_field_values?: Record<number, string> }) => {
    // Réinitialiser les contacts immédiatement pour éviter d'afficher les anciens
    setCompanyContacts([])
    
    // Ouvrir la modal immédiatement avec les données de base
    setSelectedCompany(company)
    setIsDetailModalOpen(true)
    
    // Charger les détails complets en arrière-plan
    await loadCompanyDetails(company.id)
  }

  // Ouvrir une entreprise connue par son seul numéro (depuis l'alerte de doublon).
  const ouvrirEntreprise = async (companyId: number) => {
    setCompanyContacts([])
    setSelectedCompany(null)
    setIsDetailModalOpen(true)
    await loadCompanyDetails(companyId)
  }

  // Fonction pour éditer une entreprise
  const handleEditCompany = async (company: Company & { custom_field_values?: Record<number, string> }) => {
    try {
      // Charger les détails complets de l'entreprise avec les champs personnalisés
      const response = await apiService.getCompany(company.id)
      
      if (response.success && response.data) {
        const companyDetails = response.data
        
        // Précharger les données de base dans le formulaire
        setCreateForm({
          name: companyDetails.name || '',
          website: companyDetails.website || '',
          street_address: companyDetails.street_address || '',
          address_complement: companyDetails.address_complement || '',
          postal_code: companyDetails.postal_code || '',
          city: companyDetails.city || ''
        })
        
        // Précharger les valeurs des champs personnalisés
        const customFieldsValues: Record<number, string> = {}
        
        // Vérifier la structure custom_fields (API détaillée)
        const customFieldsData = (companyDetails as any).custom_fields
        if (customFieldsData && Array.isArray(customFieldsData)) {
          for (let i = 0; i < customFieldsData.length; i++) {
            const fieldData = customFieldsData[i]
            if (fieldData.value && fieldData.field && fieldData.field.id) {
              let value = fieldData.value
              // Formater automatiquement le SIRET si c'est un champ SIRET
              if (fieldData.field.name && fieldData.field.name.toUpperCase().includes('SIRET')) {
                value = formatSiret(value)
              }
              customFieldsValues[fieldData.field.id] = value
            }
          }
        }
        
        // Définir l'entreprise à éditer et passer en mode édition
        setCompanyToEdit(companyDetails)
        setIsEditMode(true)
        
        // Définir les valeurs des champs personnalisés
        setCustomFieldValues(customFieldsValues)

        /* L'état ENREGISTRÉ du suivi, figé à l'ouverture. C'est lui qui décide
           du verrou : une case déjà cochée en base ne se décoche pas ici, elle
           se libère quand l'espace correspondant est retiré côté UP-PROJETS.
           Se fier à la valeur courante du formulaire laisserait décocher
           quelque chose qu'on vient tout juste de cocher — et surtout, elle
           changerait à chaque clic. */
        const champSuivi = customFields.find(
          (f) => f.name.trim().toLowerCase() === CHAMP_SUIVI.toLowerCase(),
        )
        setSuiviEnregistre(champSuivi ? (customFieldsValues[champSuivi.id] ?? '') : '')
        
        // Ouvrir la modal (l'useEffect chargera les champs mais préservera les valeurs)
        setIsCreateModalOpen(true)
      } else {
        toast({
          variant: "destructive",
          title: "❌ Erreur",
          description: "Impossible de charger les détails de l'entreprise pour l'édition."
        })
      }
    } catch (error) {
      console.error('Erreur lors du chargement pour l\'édition:', error)
      toast({
        variant: "destructive",
        title: "❌ Erreur",
        description: "Une erreur est survenue lors du chargement pour l'édition."
      })
    }
  }

  // Fonction pour ouvrir la modal en mode création (force la réinitialisation)
  const handleOpenAddModal = () => {
    // Forcer la réinitialisation de tous les états
    setIsEditMode(false)
    setCompanyToEdit(null)
    
    // Réinitialiser le formulaire
    setCreateForm({
      name: '',
      website: '',
      street_address: '',
      address_complement: '',
      postal_code: '',
      city: ''
    })
    
    // Réinitialiser les champs personnalisés et erreurs
    setCustomFieldValues({})
    setCreateErrors({})
    
    // Ouvrir la modal
    setIsCreateModalOpen(true)
  }

     // Fonction pour fermer la modal et réinitialiser les états
  const handleCloseModal = () => {
    setIsCreateModalOpen(false)
    setIsEditMode(false)
    setCompanyToEdit(null)
    setCreateForm({
      name: '',
      website: '',
      street_address: '',
      address_complement: '',
      postal_code: '',
      city: ''
    })
    setCustomFieldValues({})
    setCreateErrors({})
  }

  // Fonction pour fermer la modal de détails et réinitialiser les contacts
  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false)
    setSelectedCompany(null)
    setCompanyContacts([])
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <Building2 className="w-6 h-6 text-blue-600" />
            <span>Entreprises</span>
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Gérez vos entreprises clientes et prospects
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Dialog open={isColumnsModalOpen} onOpenChange={setIsColumnsModalOpen}>
            <DialogTrigger asChild>
          <Button variant="outline" className="flex items-center space-x-2">
            <Settings className="w-4 h-4" />
            <span>Colonnes visibles</span>
          </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] rounded-2xl sm:rounded-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center space-x-2">
                  <Settings className="w-5 h-5" />
                  <span>Gérer les colonnes</span>
                </DialogTitle>
                <DialogDescription>
                  Choisissez les colonnes à afficher dans le tableau (maximum 5 colonnes pour un affichage optimal)
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Boutons d'action rapide */}
                <div className="flex space-x-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllColumns}
                    className="text-blue-600"
                    disabled={selectedColumns.length === 5}
                  >
                    Tout sélectionner
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={deselectAllColumns}
                    className="text-gray-600"
                    disabled={selectedColumns.length === 0}
                  >
                    Tout désélectionner
                  </Button>
                </div>

                {/* Indicateur de limite */}
                <div className="text-sm text-gray-500">
                  {selectedColumns.length}/5 colonnes sélectionnées
                </div>

                {/* Liste des colonnes */}
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {/* Colonnes standards */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Champs standards</h4>
                    <div className="space-y-2">
                      {availableColumns
                        .filter(col => col.type === 'standard')
                        .map((column) => (
                          <div key={column.id} className="flex items-center justify-between">
                            <span className="text-sm">{column.label}</span>
                            <Button
                              variant={column.visible ? "default" : "outline"}
                              size="sm"
                              className={`w-12 h-8 ${column.visible ? 'bg-blue-600' : ''}`}
                              onClick={() => toggleColumn(column.id)}
                              disabled={!column.visible && selectedColumns.length >= 5}
                            >
                              {column.visible ? '✓' : ''}
                            </Button>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Colonnes personnalisées */}
                  {availableColumns.some(col => col.type === 'custom') && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        Champs personnalisés
                        <Badge variant="secondary" className="ml-2 text-xs">
                          Personnalisé
                        </Badge>
                      </h4>
                      <div className="space-y-2">
                        {availableColumns
                          .filter(col => col.type === 'custom')
                          .map((column) => (
                            <div key={column.id} className="flex items-center justify-between">
                              <span className="text-sm">{column.label}</span>
                              <Button
                                variant={column.visible ? "default" : "outline"}
                                size="sm"
                                className={`w-12 h-8 ${column.visible ? 'bg-blue-600' : ''}`}
                                onClick={() => toggleColumn(column.id)}
                                disabled={!column.visible && selectedColumns.length >= 5}
                              >
                                {column.visible ? '✓' : ''}
                              </Button>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={() => setIsColumnsModalOpen(false)}>
                  Terminé
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Modal d'ajout d'entreprise */}
          <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
            <DialogTrigger asChild>
              <Button 
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700"
                onClick={handleOpenAddModal}
              >
                <Plus className="w-4 h-4" />
                <span>Ajouter une entreprise</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col rounded-2xl sm:rounded-2xl">
              <DialogHeader className="flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <DialogTitle>{isEditMode ? 'Modifier l\'entreprise' : 'Ajouter une entreprise'}</DialogTitle>
                    <DialogDescription>
                      {isEditMode ? 'Modifiez les informations de cette entreprise' : 'Créez une nouvelle entreprise dans votre CRM'}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6">
                {/* Section Identité de l'entreprise */}
                <div className="space-y-4">
                <h3 className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  <Building2 className="w-4 h-4" /> Identité de l'entreprise
                </h3>
                {/* Nom de l'entreprise et Site web sur la même ligne */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company-name" className="flex items-center gap-1.5">
                      Nom de l'entreprise *
                      <span className="inline-flex items-center gap-1 text-xs font-normal text-blue-600">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3">
                          <path d="M12 3l1.9 5.8L20 11l-5.8 1.9L12 20l-1.9-5.8L4 11l6.1-2.2L12 3z" />
                        </svg>
                        suggéré depuis l'annuaire officiel
                      </span>
                    </Label>
                    <CompanyAutocomplete
                      value={createForm.name}
                      onChange={(v) => handleCreateFormChange('name', v)}
                      onSelect={applyCompanySuggestion}
                      placeholder="Nom de l'entreprise ou SIRET…"
                      errorClass={createErrors.name ? 'border-red-500' : ''}
                    />
                    {createErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{createErrors.name}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company-website">
                      Site web
                    </Label>
                    <div className="relative">
                      <ExternalLink className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <Input
                        id="company-website"
                        placeholder="https://example.com"
                        value={createForm.website}
                        onChange={(e) => handleCreateFormChange('website', e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                </div>

                <AlerteDoublon
                  type="entreprise"
                  nom={createForm.name}
                  siret={customFieldValues[customFields.find((f) => f.name.trim().toUpperCase() === 'SIRET')?.id ?? -1] ?? ''}
                  sauf={isEditMode ? companyToEdit?.id : undefined}
                  onOuvrir={(id) => { handleCloseModal(); void ouvrirEntreprise(id) }}
                  onFusionner={isEditMode && companyToEdit
                    ? (id) => { const garde = companyToEdit.id; handleCloseModal(); setFusion({ ficheId: garde, autreId: id }) }
                    : undefined}
                />

                {/* Adresse */}
                <div className="space-y-2">
                  <Label htmlFor="company-address">
                    Adresse
                  </Label>
                  <div className="relative">
                    <MapPin className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <Input
                      id="company-address"
                      placeholder="Rue et numéro"
                      value={createForm.street_address}
                      onChange={(e) => handleCreateFormChange('street_address', e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>

                {/* Complément d'adresse */}
                <div className="space-y-2">
                  <Label htmlFor="company-address-complement">
                    Complément d'adresse
                  </Label>
                  <Input
                    id="company-address-complement"
                    placeholder="Bâtiment, étage, etc."
                    value={createForm.address_complement}
                    onChange={(e) => handleCreateFormChange('address_complement', e.target.value)}
                  />
                </div>

                {/* Code postal et Ville sur la même ligne */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company-postal-code">
                      Code postal
                    </Label>
                    <Input
                      id="company-postal-code"
                      placeholder="Code postal"
                      value={createForm.postal_code}
                      onChange={(e) => handleCreateFormChange('postal_code', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company-city">
                      Ville
                    </Label>
                    <Input
                      id="company-city"
                      placeholder="Ville"
                      value={createForm.city}
                      onChange={(e) => handleCreateFormChange('city', e.target.value)}
                    />
                  </div>
                </div>
                </div>

                {/* Sections champs perso : Identifiants légaux (SIRET + TVA)
                    regroupés à part, puis les autres champs personnalisés. */}
                {loadingCustomFields ? (
                  <div className="border-t border-gray-200 pt-6 flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-sm text-gray-500">Chargement...</span>
                  </div>
                ) : (() => {
                  const siretField = findSiretField()
                  const vatField = findVatField()
                  const legalIds = [siretField?.id, vatField?.id].filter(
                    (v): v is number => typeof v === 'number',
                  )
                  const legalFields = [siretField, vatField].filter(Boolean) as typeof customFields
                  const suiviField = findSuiviField()
                  const otherFields = customFields.filter(
                    (f) => !legalIds.includes(f.id) && f.id !== suiviField?.id,
                  )
                  return (
                    <>
                      {/* Zone Identifiants légaux (annuaire officiel) */}
                      {legalFields.length > 0 && (
                        <div className="border-t border-gray-200 pt-6 space-y-3">
                          <h3 className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                            <FileCheck className="w-4 h-4" /> Identifiants légaux
                            <span className="inline-flex items-center gap-1 text-[11px] font-normal normal-case tracking-normal text-blue-600">
                              <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3">
                                <path d="M12 3l1.9 5.8L20 11l-5.8 1.9L12 20l-1.9-5.8L4 11l6.1-2.2L12 3z" />
                              </svg>
                              renseignés depuis l'annuaire officiel
                            </span>
                          </h3>
                          <div className="rounded-xl bg-blue-50/60 ring-1 ring-blue-200 divide-y divide-blue-100 overflow-hidden">
                            {legalFields.map((field) => (
                              <div
                                key={field.id}
                                className="flex items-center justify-between gap-4 px-4 py-3"
                              >
                                <Label htmlFor={`custom_field_${field.id}`} className="text-sm font-medium text-slate-700">
                                  {field.name}
                                  {field.required === 1 && <span className="text-red-500 ml-1">*</span>}
                                </Label>
                                <div className="shrink-0 w-52 flex justify-end">
                                  {renderCustomField(field)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Suivi dans UP-PROJETS — une décision d'organisation,
                          pas une caractéristique de l'entreprise, d'où sa
                          section à part. */}
                      {suiviField && (
                        <div className="border-t border-gray-200 pt-6 space-y-3">
                          <h3 className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                            <FolderKanban className="w-4 h-4" /> Suivi dans UP-PROJETS
                          </h3>
                          <p className="text-xs text-gray-500 -mt-1">
                            Ce que vous cochez ici est créé dans UP-PROJETS. Une case cochée ne se
                            décoche pas d&apos;ici : elle se libère quand l&apos;espace correspondant
                            y est supprimé.
                          </p>
                          {renderSuiviUpProjets(suiviField)}
                        </div>
                      )}

                      {/* Autres champs personnalisés */}
                      {otherFields.length > 0 && (
                        <div className="border-t border-gray-200 pt-6 space-y-4">
                          <h3 className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                            <SlidersHorizontal className="w-4 h-4" /> Champs personnalisés
                          </h3>
                          <div className="rounded-xl ring-1 ring-gray-200 divide-y divide-gray-100 overflow-hidden">
                            {otherFields.map((field) => (
                              <div
                                key={field.id}
                                className="flex items-center justify-between gap-4 px-4 py-3"
                              >
                                <Label htmlFor={`custom_field_${field.id}`} className="text-sm font-medium text-gray-700">
                                  {field.name}
                                  {field.required === 1 && <span className="text-red-500 ml-1">*</span>}
                                </Label>
                                <div className="shrink-0 w-48 flex justify-end">
                                  {renderCustomField(field)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )
                })()}
                </div>
              </div>

              <DialogFooter className="flex-shrink-0">
                <Button 
                  variant="outline" 
                  onClick={handleCloseModal}
                  disabled={isCreating}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={handleCreateCompany}
                  disabled={isCreating}
                  className="bg-blue-600 hover:bg-blue-700 mr-8"
                >
                  {isCreating 
                    ? (isEditMode ? 'Modification en cours...' : 'Ajout en cours...') 
                    : (isEditMode ? 'Modifier l\'entreprise' : 'Ajouter')
                  }
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Rechercher une entreprise..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Filtres avancés */}
      <div className="space-y-4">
      <div className="flex items-center space-x-4">
          <Button 
            variant="outline" 
            className="flex items-center space-x-2"
            onClick={() => setShowFilters(!showFilters)}
          >
              <Filter className="w-4 h-4" />
              <span>Filtres avancés</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
        
        {/* Indicateur de résultats */}
        <div className="text-sm text-gray-600">
          {loading ? 'Chargement...' : `${totalItems} entreprise${totalItems > 1 ? 's' : ''} trouvée${totalItems > 1 ? 's' : ''}`}
        </div>
        </div>

        {/* Section des filtres avancés */}
        {showFilters && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <div className="space-y-3">
              {advancedFilters.map((filter, index) => (
                <div key={index} className="grid grid-cols-12 gap-3 items-end">
                  {/* Nom */}
                  <div className="col-span-3">
                    <Label className="text-sm font-medium text-gray-700">Nom</Label>
                    <Select 
                      value={filter.field} 
                      onValueChange={(value) => updateAdvancedFilter(index, 'field', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Champs standards */}
                        <div className="px-2 py-1.5 text-sm font-medium text-gray-500 bg-gray-100">
                          Champs standards
                        </div>
                        {availableFilterFields
                          .filter(field => field.type === 'standard')
                          .map((field) => (
                            <SelectItem key={field.value} value={field.value}>
                              {field.label}
                            </SelectItem>
                          ))}
                        
                        {/* Champs personnalisés */}
                        {availableFilterFields.some(field => field.type === 'custom') && (
                          <>
                            <div className="px-2 py-1.5 text-sm font-medium text-gray-500 bg-gray-100 mt-1">
                              Champs personnalisés
                            </div>
                            {availableFilterFields
                              .filter(field => field.type === 'custom')
                              .map((field) => (
                                <SelectItem key={field.value} value={field.value}>
                                  {field.label}
                                </SelectItem>
                              ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Est égal à */}
                  <div className="col-span-3">
                    <Label className="text-sm font-medium text-gray-700">Opérateur</Label>
                    <Select 
                      value={filter.operator} 
                      onValueChange={(value) => updateAdvancedFilter(index, 'operator', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const selectedField = availableFilterFields.find(f => f.value === filter.field)
                          const operators = getFilterOperators(selectedField?.fieldType)
                          return operators.map((operator) => (
                            <SelectItem key={operator.value} value={operator.value}>
                              {operator.label}
                            </SelectItem>
                          ))
                        })()}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Valeur */}
                  <div className="col-span-5">
                    <Label className="text-sm font-medium text-gray-700">Valeur</Label>
                    {renderValueField(filter, index)}
                  </div>

                  {/* Bouton supprimer */}
                  <div className="col-span-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAdvancedFilter(index)}
                      className="h-10 w-10 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}

                          {/* Opérateur logique entre les filtres */}
            {advancedFilters.length > 1 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Opérateur logique entre les filtres
                </Label>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="filter-and-companies"
                      name="filterLogicCompanies"
                      value="AND"
                      checked={filterLogicOperator === 'AND'}
                      onChange={(e) => setFilterLogicOperator(e.target.value as 'AND' | 'OR')}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="filter-and-companies" className="text-sm text-gray-700">
                      <span className="font-medium text-blue-600">ET</span> (tous les filtres doivent correspondre)
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="filter-or-companies"
                      name="filterLogicCompanies"
                      value="OR"
                      checked={filterLogicOperator === 'OR'}
                      onChange={(e) => setFilterLogicOperator(e.target.value as 'AND' | 'OR')}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="filter-or-companies" className="text-sm text-gray-700">
                      <span className="font-medium text-blue-600">OU</span> (au moins un filtre doit correspondre)
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Message si aucun filtre */}
            {advancedFilters.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                Aucun filtre défini. Utilisez le filtre rapide ci-dessus ou cliquez sur "Ajouter un filtre" pour commencer.
              </div>
            )}
          </div>

          {/* Boutons d'action */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-3">
              <Button
                onClick={addAdvancedFilter}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Ajouter un filtre</span>
              </Button>
              <Button
                onClick={() => {
                  setAdvancedFilters([])
                  setCurrentPage(1)
                }}
                variant="outline"
                disabled={advancedFilters.length === 0}
              >
                Appliquer les filtres
              </Button>
              <Button
                variant="outline"
                onClick={resetAdvancedFilters}
                disabled={advancedFilters.length === 0}
              >
                Réinitialiser
              </Button>
            </div>

            {/* Bouton enregistrer les filtres */}
            {advancedFilters.length > 0 && (
              <Dialog open={isFilterSaveModalOpen} onOpenChange={setIsFilterSaveModalOpen}>
                <DialogContent className="sm:max-w-[400px] rounded-2xl sm:rounded-2xl">
                  <DialogHeader>
                    <DialogTitle>Enregistrer le filtre</DialogTitle>
                    <DialogDescription>
                      Donnez un nom à ce filtre pour le retrouver facilement plus tard.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="filter-name-companies">Nom du filtre</Label>
                      <Input
                        id="filter-name-companies"
                        placeholder="Ex: Entreprises parisiennes avec site web"
                        value={filterSaveName}
                        onChange={(e) => setFilterSaveName(e.target.value)}
                      />
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      <strong>Aperçu:</strong> {advancedFilters.length} filtre(s) avec opérateur {filterLogicOperator}
                    </div>
                  </div>

                  <DialogFooter>
                    <Button 
                      variant="outline" 
                      onClick={() => setIsFilterSaveModalOpen(false)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      onClick={saveCurrentFilters}
                      disabled={!filterSaveName.trim()}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Enregistrer
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          </div>
        )}
      </div>

      {/* Gestion des erreurs */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          <p>❌ {error}</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadCompanies}
            className="mt-2"
          >
            Réessayer
          </Button>
        </div>
      )}

      {/* Tableau */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              {selectedColumns.map((columnId) => (
                <TableHead 
                  key={columnId} 
                  className="font-medium cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort(columnId)}
                  title={`Trier par ${getColumnLabel(columnId)}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{getColumnLabel(columnId).toUpperCase()}</span>
                    <span className="ml-1 text-gray-400">{getSortIcon(columnId)}</span>
                  </div>
                </TableHead>
              ))}
              {/* La colonne qui relie les deux applications : statut du suivi
                  et bouton pour écrire, au même endroit. */}
              <TableHead className="font-medium">UP-PROJETS</TableHead>
              <TableHead className="font-medium">ACTIONS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              // Squelettes de chargement
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {selectedColumns.map((columnId) => (
                    <TableCell key={columnId}>
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </TableCell>
                  ))}
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </TableCell>
                </TableRow>
              ))
            ) : companies.length === 0 ? (
              // Aucune entreprise trouvée
              <TableRow>
                <TableCell colSpan={selectedColumns.length + 1} className="text-center py-8 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucune entreprise trouvée</p>
                  {searchTerm && (
                    <p className="text-sm mt-2">
                      Essayez de modifier vos critères de recherche
                    </p>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              // Liste des entreprises
              companies.map((company) => (
                <TableRow key={company.id} className="hover:bg-gray-50">
                  {selectedColumns.map((columnId) => (
                    <TableCell
                      key={columnId}
                      className={`cursor-pointer ${columnId === 'name' ? 'font-medium' : ''}`}
                      onClick={() => handleViewCompany(company)}
                    >
                      {renderTableCell(company, columnId)}
                  </TableCell>
                  ))}
                  <TableCell>
                    <UpProjetsCell
                      companyId={company.id}
                      suivi={suiviDeLEntreprise(company)}
                      nom={company.name}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="Modifier"
                        onClick={(e) => { e.stopPropagation(); handleEditCompany(company); }}
                      >
                        <Edit className="w-4 h-4 text-green-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="Supprimer"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCompany(company); }}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination améliorée */}
      {!loading && companies.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <p className="text-sm text-gray-700">
              Affichage de {((currentPage - 1) * itemsPerPage) + 1} à{' '}
              {Math.min(currentPage * itemsPerPage, totalItems)} sur {totalItems} résultats
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-3">
            {/* Navigation directe */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Aller à la page :</span>
              <Input
                type="number"
                min="1"
                max={totalPages}
                value={currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value)
                  if (page >= 1 && page <= totalPages) {
                    handlePageChange(page)
                  }
                }}
                className="w-16 h-8 text-center"
              />
              <span className="text-sm text-gray-600">sur {totalPages}</span>
            </div>

            {/* Boutons de pagination */}
            <div className="flex items-center space-x-1">
              {/* Première page */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={currentPage <= 1}
                className="w-8 h-8 p-0"
                title="Première page"
              >
                ««
              </Button>

              {/* Page précédente */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="flex items-center space-x-1 px-3"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Précédent</span>
              </Button>
              
              {/* Pages numérotées intelligentes */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const pages = []
                  const showPages = 5 // Nombre de pages à afficher
                  let startPage = Math.max(1, currentPage - Math.floor(showPages / 2))
                  const endPage = Math.min(totalPages, startPage + showPages - 1)
                  
                  // Ajuster si on est près de la fin
                  if (endPage - startPage < showPages - 1) {
                    startPage = Math.max(1, endPage - showPages + 1)
                  }

                  // Première page + ellipsis si nécessaire
                  if (startPage > 1) {
                    pages.push(
                      <Button
                        key={1}
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        className="w-8 h-8 p-0"
                      >
                        1
                      </Button>
                    )
                    if (startPage > 2) {
                      pages.push(
                        <span key="ellipsis1" className="px-2 text-gray-400">...</span>
                      )
                    }
                  }

                  // Pages du milieu
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <Button
                        key={i}
                        variant={currentPage === i ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                        className="w-8 h-8 p-0"
                      >
                        {i}
                      </Button>
                    )
                  }

                  // Ellipsis + dernière page si nécessaire
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="ellipsis2" className="px-2 text-gray-400">...</span>
                      )
                    }
                    pages.push(
                      <Button
                        key={totalPages}
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(totalPages)}
                        className="w-8 h-8 p-0"
                      >
                        {totalPages}
                      </Button>
                    )
                  }

                  return pages
                })()}
              </div>
              
              {/* Page suivante */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="flex items-center space-x-1 px-3"
              >
                <span className="hidden sm:inline">Suivant</span>
                <ChevronRight className="w-4 h-4" />
              </Button>

              {/* Dernière page */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage >= totalPages}
                className="w-8 h-8 p-0"
                title="Dernière page"
              >
                »»
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détails de l'entreprise */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden rounded-2xl sm:rounded-2xl">
          {selectedCompany && (
            <div className="max-h-[85vh] overflow-y-auto">
              {/* En-tête sobre avec avatar dégradé */}
              <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100">
                <div className="flex items-center gap-4 min-w-0 pr-2">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md shrink-0">
                    <Building2 className="w-7 h-7" />
                  </div>
                  <div className="min-w-0">
                    <DialogTitle className="text-xl font-bold text-slate-800 leading-tight truncate">
                      {decodeHtmlEntities(selectedCompany.name)}
                    </DialogTitle>
                    <p className="text-sm font-medium text-blue-600 mt-0.5">
                      {companyContacts.length} contact{companyContacts.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center shrink-0 pr-8">
                  <Button
                    variant="outline"
                    size="sm"
                    className="inline-flex items-center gap-2 font-semibold"
                    onClick={() => {
                      setIsDetailModalOpen(false)
                      handleEditCompany(selectedCompany)
                    }}
                  >
                    <Edit className="w-4 h-4" />
                    <span>Modifier</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="inline-flex items-center gap-2 font-semibold ml-2"
                    title="Fusionner avec une entreprise en double"
                    onClick={() => {
                      setIsDetailModalOpen(false)
                      setFusion({ ficheId: selectedCompany.id })
                    }}
                  >
                    <GitMerge className="w-4 h-4" />
                    <span>Fusionner</span>
                  </Button>
                </div>
              </div>

              {/* Zones colorées par catégorie */}
              <div className="p-6 space-y-4">
                {/* Zone Informations (bleu) */}
                <div className="rounded-2xl border border-blue-100 bg-blue-50/50 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-100/70 border-b border-blue-100">
                    <Building2 className="w-4 h-4 text-blue-700" />
                    <h3 className="text-xs font-bold text-blue-800 uppercase tracking-wide">Informations</h3>
                  </div>
                  <div className="divide-y divide-blue-100">
                    {/* Site web */}
                    {selectedCompany.website && (
                      <a
                        href={selectedCompany.website.startsWith('http') ? selectedCompany.website : `https://${selectedCompany.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-blue-100/40 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4 text-blue-500 shrink-0" />
                        <span className="text-xs text-slate-400 w-20 shrink-0">Site web</span>
                        <span className="font-semibold text-blue-600 truncate">{decodeHtmlEntities(selectedCompany.website)}</span>
                      </a>
                    )}
                    {/* Adresse */}
                    {(selectedCompany.street_address || selectedCompany.city || selectedCompany.postal_code) && (
                      <div className="flex items-start gap-3 px-4 py-3">
                        <MapPin className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                        <span className="text-xs text-slate-400 w-20 shrink-0 mt-0.5">Adresse</span>
                        <div className="font-semibold text-slate-800">
                          {selectedCompany.street_address && (
                            <p>{decodeHtmlEntities(selectedCompany.street_address)}</p>
                          )}
                          {selectedCompany.address_complement && (
                            <p>{decodeHtmlEntities(selectedCompany.address_complement)}</p>
                          )}
                          {(selectedCompany.postal_code || selectedCompany.city) && (
                            <p>
                              {selectedCompany.postal_code && `${decodeHtmlEntities(selectedCompany.postal_code)} `}
                              {selectedCompany.city && decodeHtmlEntities(selectedCompany.city)}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Date de création */}
                    <div className="flex items-center gap-3 px-4 py-3">
                      <Calendar className="w-4 h-4 text-blue-500 shrink-0" />
                      <span className="text-xs text-slate-400 w-20 shrink-0">Ajoutée le</span>
                      <span className="font-semibold text-slate-800">{formatDate(selectedCompany.created_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Zone Identifiants légaux (bleu) — SIRET + TVA */}
                {!loadingCompanyDetails && (() => {
                  const siretField = findSiretField()
                  const vatField = findVatField()
                  // Résout la valeur d'un champ perso depuis l'API détaillée
                  // ou, à défaut, depuis les valeurs du tableau.
                  const resolve = (fid?: number) => {
                    if (!fid) return undefined
                    const fromDetailed = selectedCompany.custom_fields?.find(
                      (c) => c.field.id === fid,
                    )?.value
                    return fromDetailed || selectedCompany.custom_field_values?.[fid]
                  }
                  const siretValue = resolve(siretField?.id)
                  let vatValue = resolve(vatField?.id)
                  let vatComputed = false
                  // Repli : si la TVA n'est pas stockée mais qu'on a un SIRET,
                  // on la calcule à la volée (déterministe depuis le SIREN).
                  if (!vatValue && siretValue) {
                    const computed = computeFrenchVat(siretValue)
                    if (computed) {
                      vatValue = formatFrenchVat(computed)
                      vatComputed = true
                    }
                  }
                  const rows: { id: string; name: string; value: string; computed?: boolean }[] = []
                  if (siretField && siretValue) {
                    rows.push({ id: 'siret', name: siretField.name, value: siretValue })
                  }
                  if (vatValue) {
                    rows.push({
                      id: 'vat',
                      name: vatField?.name || 'TVA intracommunautaire',
                      value: vatValue,
                      computed: vatComputed,
                    })
                  }
                  if (rows.length === 0) return null
                  return (
                    <div className="rounded-2xl border border-blue-100 bg-blue-50/50 overflow-hidden">
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-100/70 border-b border-blue-100">
                        <FileCheck className="w-4 h-4 text-blue-700" />
                        <h3 className="text-xs font-bold text-blue-800 uppercase tracking-wide">Identifiants légaux</h3>
                      </div>
                      <div className="divide-y divide-blue-100">
                        {rows.map((r) => (
                          <div key={r.id} className="flex items-center gap-3 px-4 py-3">
                            <span className="text-xs text-slate-400 w-28 shrink-0">{decodeHtmlEntities(r.name)}</span>
                            <span className="font-semibold text-slate-800 font-mono">{decodeHtmlEntities(r.value)}</span>
                            {r.computed && (
                              <span className="text-[10px] text-blue-500 italic ml-1">calculé</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()}

                {/* Zone Suivi dans UP-PROJETS (indigo).

                    Toujours affichée, y compris quand rien n'est coché : « pas
                    encore suivie » est une information, pas une absence. Une
                    section qui disparaît quand elle est vide oblige à ouvrir la
                    modification pour savoir où l'on en est. */}
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-indigo-100/70 border-b border-indigo-100">
                    <FolderKanban className="w-4 h-4 text-indigo-700" />
                    <h3 className="text-xs font-bold text-indigo-800 uppercase tracking-wide">Suivi dans UP-PROJETS</h3>
                  </div>
                  {(() => {
                    const champ = findSuiviField()
                    let valeur = ''
                    if (champ) {
                      const depuisDetail = selectedCompany.custom_fields?.find(c => c.field.id === champ.id)?.value
                      valeur = depuisDetail ?? selectedCompany.custom_field_values?.[champ.id] ?? ''
                    }
                    const coches = valeur.split(',').filter(Boolean)
                    const lignes = [
                      { valeur: SUIVI_DOCUMENTAIRE, libelle: 'Espace documentaire' },
                      { valeur: SUIVI_ACTIONS, libelle: 'Suivi des actions' },
                    ]
                    return (
                      <div className="divide-y divide-indigo-100">
                        {lignes.map((l) => {
                          const actif = coches.includes(l.valeur)
                          return (
                            <div key={l.valeur} className="flex items-center gap-3 px-4 py-3">
                              <span className="text-xs text-slate-400 w-36 shrink-0">{l.libelle}</span>
                              <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${
                                actif ? 'text-indigo-800' : 'text-slate-400'
                              }`}>
                                <span className={`w-2 h-2 rounded-full ${actif ? 'bg-indigo-600' : 'bg-slate-300'}`} />
                                {actif ? 'Actif' : 'Non suivi'}
                              </span>
                            </div>
                          )
                        })}
                        {coches.length === 0 && (
                          <p className="text-xs text-slate-500 px-4 py-2.5">
                            Cette entreprise n&apos;est pas suivie dans UP-PROJETS. Cochez ce qu&apos;il
                            vous faut depuis « Modifier ».
                          </p>
                        )}
                      </div>
                    )
                  })()}
                </div>

                {/* Zone Champs personnalisés (ambre) — hors identifiants légaux */}
                <div className="rounded-2xl border border-amber-100 bg-amber-50/50 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-100/70 border-b border-amber-100">
                    <SlidersHorizontal className="w-4 h-4 text-amber-700" />
                    <h3 className="text-xs font-bold text-amber-800 uppercase tracking-wide">Champs personnalisés</h3>
                  </div>
                  {loadingCompanyDetails ? (
                    <div className="divide-y divide-amber-100">
                      {Array.from({ length: 2 }).map((_, index) => (
                        <div key={index} className="flex items-center gap-3 px-4 py-3">
                          <div className="h-3 bg-amber-200/60 rounded animate-pulse w-24"></div>
                          <div className="h-4 bg-amber-200/60 rounded animate-pulse w-32"></div>
                        </div>
                      ))}
                    </div>
                  ) : (() => {
                    // Champs perso avec valeur, en excluant les identifiants légaux
                    const rows: { id: number; name: string; value: string }[] = []
                    if (selectedCompany.custom_fields && selectedCompany.custom_fields.length > 0) {
                      for (const c of selectedCompany.custom_fields) {
                        if (c.value && !isLegalField(c.field.id) && !isSuiviField(c.field.id)) {
                          rows.push({ id: c.field.id, name: c.field.name, value: displayCustomValue(c.field, c.value) })
                        }
                      }
                    } else if (selectedCompany.custom_field_values) {
                      for (const cf of customFields) {
                        const v = selectedCompany.custom_field_values[cf.id]
                        if (v && !isLegalField(cf.id) && !isSuiviField(cf.id)) {
                          rows.push({ id: cf.id, name: cf.name, value: displayCustomValue(cf, v) })
                        }
                      }
                    }
                    if (rows.length === 0) {
                      return <p className="text-sm text-slate-400 px-4 py-3">Aucun champ personnalisé renseigné</p>
                    }
                    return (
                      <div className="divide-y divide-amber-100">
                        {rows.map((r) => (
                          <div key={r.id} className="flex items-center gap-3 px-4 py-3">
                            <span className="text-xs text-slate-400 w-28 shrink-0">{decodeHtmlEntities(r.name)}</span>
                            <span className="font-semibold text-slate-800">{decodeHtmlEntities(r.value)}</span>
                          </div>
                        ))}
                      </div>
                    )
                  })()}
                </div>

                {/* Zone Contacts (émeraude) */}
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-100/70 border-b border-emerald-100">
                    <Users className="w-4 h-4 text-emerald-700" />
                    <h3 className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Contacts ({companyContacts.length})</h3>
                    <button
                      type="button"
                      onClick={openAddContactToCompany}
                      className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors"
                      title="Ajouter un contact à cette entreprise"
                    >
                      <UserPlus className="w-3.5 h-3.5" /> Ajouter
                    </button>
                  </div>
                  <div className="p-3">
                    {loadingCompanyContacts ? (
                      <div className="space-y-2">
                        {Array.from({ length: 3 }).map((_, index) => (
                          <div key={index} className="flex items-center gap-3 p-2.5 bg-white rounded-lg ring-1 ring-emerald-100">
                            <div className="w-9 h-9 bg-slate-200 rounded-full animate-pulse shrink-0"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-3 bg-slate-200 rounded animate-pulse w-32"></div>
                              <div className="h-3 bg-slate-200 rounded animate-pulse w-24"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : companyContacts.length > 0 ? (
                      <div className="space-y-2">
                        {companyContacts.map((contact) => (
                          <div
                            key={contact.id}
                            className="w-full flex items-center gap-2 p-2.5 rounded-lg bg-white ring-1 ring-emerald-100 hover:ring-emerald-300 hover:shadow-sm transition-all"
                          >
                            <button
                              type="button"
                              onClick={() => handleViewCompanyContact(contact.id)}
                              className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer"
                            >
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                                {`${contact.first_name?.[0] ?? ''}${contact.last_name?.[0] ?? ''}`.toUpperCase() || '?'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-slate-800 truncate flex items-center gap-2">
                                  <span className="truncate">{contact.last_name} {contact.first_name}</span>
                                  {contact.upspot_compte_id && (
                                    <UpSpotBadge
                                      compteId={contact.upspot_compte_id}
                                      role={contact.upspot_role}
                                      statut={contact.upspot_statut}
                                      invitationEnAttente={contact.upspot_invitation_en_attente}
                                    />
                                  )}
                                </p>
                                <div className="flex items-center gap-4 text-sm text-slate-500">
                                  {contact.email && (
                                    <span className="flex items-center gap-1 min-w-0">
                                      <Mail className="w-3 h-3 shrink-0" />
                                      <span className="truncate">{contact.email}</span>
                                    </span>
                                  )}
                                  {contact.phone && (
                                    <span className="flex items-center gap-1 shrink-0">
                                      <Phone className="w-3 h-3" />
                                      <span>{contact.phone}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setChoixUpSpot('desactiver')
                                setSuppressionConfirmee(false)
                                setContactToRemove(contact)
                              }}
                              className="p-2 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
                              title="Supprimer ce contact"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <Users className="w-10 h-10 text-emerald-200 mx-auto mb-2" />
                        <p className="text-sm text-slate-400">Aucun contact associé à cette entreprise</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Fermer */}
                <Button
                  variant="outline"
                  className="w-full font-semibold"
                  onClick={handleCloseDetailModal}
                >
                  Fermer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de détails d'un contact (empilée sur la modal entreprise) */}
      <Dialog
        open={isContactDetailOpen}
        onOpenChange={(open) => {
          setIsContactDetailOpen(open)
          if (!open) setViewedContact(null)
        }}
      >
        <DialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden rounded-2xl sm:rounded-2xl">
          {loadingViewedContact && !viewedContact ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
              <p className="text-sm text-slate-400">Chargement du contact…</p>
            </div>
          ) : viewedContact ? (
            <div className="max-h-[85vh] overflow-y-auto">
              {/* En-tête sobre avec avatar dégradé */}
              <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100">
                <div className="flex items-center gap-4 min-w-0 pr-2">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-lg font-bold shadow-md shrink-0">
                    {`${viewedContact.first_name?.[0] ?? ''}${viewedContact.last_name?.[0] ?? ''}`.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0">
                    <DialogTitle className="text-xl font-bold text-slate-800 leading-tight truncate">
                      {viewedContact.first_name} {viewedContact.last_name}
                    </DialogTitle>
                    <p className="text-sm font-medium text-blue-600 mt-0.5 truncate">
                      {viewedContact.company_name || decodeHtmlEntities(selectedCompany?.name || '') || 'Aucune entreprise'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Zones colorées par catégorie */}
              <div className="p-6 space-y-4">
                {/* Zone Coordonnées (bleu) */}
                <div className="rounded-2xl border border-blue-100 bg-blue-50/50 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-100/70 border-b border-blue-100">
                    <ContactIcon className="w-4 h-4 text-blue-700" />
                    <h3 className="text-xs font-bold text-blue-800 uppercase tracking-wide">Coordonnées</h3>
                  </div>
                  <div className="divide-y divide-blue-100">
                    {viewedContact.email ? (
                      <a href={`mailto:${viewedContact.email}`} className="flex items-center gap-3 px-4 py-3 hover:bg-blue-100/40 transition-colors">
                        <Mail className="w-4 h-4 text-blue-500 shrink-0" />
                        <span className="text-xs text-slate-400 w-20 shrink-0">Email</span>
                        <span className="font-semibold text-slate-800 truncate">{viewedContact.email}</span>
                      </a>
                    ) : (
                      <div className="flex items-center gap-3 px-4 py-3">
                        <Mail className="w-4 h-4 text-blue-400 shrink-0" />
                        <span className="text-xs text-slate-400 w-20 shrink-0">Email</span>
                        <span className="text-slate-400">Non renseigné</span>
                      </div>
                    )}
                    {viewedContact.phone ? (
                      <a href={`tel:${viewedContact.phone}`} className="flex items-center gap-3 px-4 py-3 hover:bg-blue-100/40 transition-colors">
                        <Phone className="w-4 h-4 text-blue-500 shrink-0" />
                        <span className="text-xs text-slate-400 w-20 shrink-0">Téléphone</span>
                        <span className="font-semibold text-slate-800">{viewedContact.phone}</span>
                      </a>
                    ) : (
                      <div className="flex items-center gap-3 px-4 py-3">
                        <Phone className="w-4 h-4 text-blue-400 shrink-0" />
                        <span className="text-xs text-slate-400 w-20 shrink-0">Téléphone</span>
                        <span className="text-slate-400">Non renseigné</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Zone Entreprise (indigo) */}
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-indigo-100/70 border-b border-indigo-100">
                    <Building2 className="w-4 h-4 text-indigo-700" />
                    <h3 className="text-xs font-bold text-indigo-800 uppercase tracking-wide">Entreprise</h3>
                  </div>
                  <div className="px-4 py-3">
                    <span className="font-semibold text-slate-800">
                      {viewedContact.company_name || decodeHtmlEntities(selectedCompany?.name || '') || 'Non renseignée'}
                    </span>
                  </div>
                </div>

                {/* Zone Accès UP-SPOT (violet) — UP-CRM est la porte d'entrée des comptes */}
                <UpSpotPanel
                  contactId={viewedContact.id}
                  email={viewedContact.email}
                  onChange={() => { if (selectedCompany) void loadCompanyContacts(selectedCompany.id) }}
                  lienGestion={`/contacts?modifier=${viewedContact.id}`}
                />

                {/* Zone Champs personnalisés (ambre) */}
                <div className="rounded-2xl border border-amber-100 bg-amber-50/50 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-100/70 border-b border-amber-100">
                    <SlidersHorizontal className="w-4 h-4 text-amber-700" />
                    <h3 className="text-xs font-bold text-amber-800 uppercase tracking-wide">Champs personnalisés</h3>
                  </div>
                  {viewedContact.custom_fields && viewedContact.custom_fields.filter((c: any) => c.value).length > 0 ? (
                    <div className="divide-y divide-amber-100">
                      {viewedContact.custom_fields.map((customFieldData: any) => {
                        if (!customFieldData.value) return null
                        return (
                          <div key={customFieldData.field.id} className="flex items-center gap-3 px-4 py-3">
                            <span className="text-xs text-slate-400 w-28 shrink-0">{decodeHtmlEntities(customFieldData.field.name)}</span>
                            <span className="font-semibold text-slate-800">{decodeHtmlEntities(customFieldData.value)}</span>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 px-4 py-3">Aucun champ personnalisé renseigné</p>
                  )}
                </div>

                {/* Action Fermer (revient sur la modal entreprise) */}
                <Button
                  variant="outline"
                  className="w-full font-semibold"
                  onClick={() => {
                    setIsContactDetailOpen(false)
                    setViewedContact(null)
                  }}
                >
                  Fermer
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Ajouter un contact directement depuis la fiche entreprise (modal empilée) */}
      <Dialog open={isAddContactOpen} onOpenChange={(open) => { if (!open) setIsAddContactOpen(false) }}>
        <DialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden rounded-2xl sm:rounded-2xl">
          <div className="max-h-[85vh] overflow-y-auto">
            {/* En-tête */}
            <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100">
              <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                <UserPlus className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-xl font-bold text-slate-800 leading-tight">Nouveau contact</DialogTitle>
                <DialogDescription>
                  Pour <span className="font-semibold text-blue-600">{selectedCompany ? decodeHtmlEntities(selectedCompany.name) : ''}</span>
                </DialogDescription>
              </div>
            </div>

            {/* Corps */}
            <div className="px-6 py-4 space-y-6">
              {/* Section Coordonnées */}
              <div className="space-y-4">
                <h3 className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  <ContactIcon className="w-4 h-4" /> Coordonnées
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-contact-first-name">Prénom *</Label>
                    <Input
                      id="new-contact-first-name"
                      value={newContactForm.first_name}
                      onChange={(e) => setNewContactForm({ ...newContactForm, first_name: e.target.value })}
                      placeholder="Entrez le prénom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-contact-last-name">Nom *</Label>
                    <Input
                      id="new-contact-last-name"
                      value={newContactForm.last_name}
                      onChange={(e) => setNewContactForm({ ...newContactForm, last_name: e.target.value })}
                      placeholder="Entrez le nom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-contact-email">Email *</Label>
                    <Input
                      id="new-contact-email"
                      type="email"
                      value={newContactForm.email}
                      onChange={(e) => setNewContactForm({ ...newContactForm, email: e.target.value })}
                      placeholder="contact@exemple.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-contact-phone">Téléphone</Label>
                    <Input
                      id="new-contact-phone"
                      value={newContactForm.phone}
                      onChange={(e) => setNewContactForm({ ...newContactForm, phone: e.target.value })}
                      placeholder="01 23 45 67 89"
                    />
                  </div>
                </div>
                {/* Entreprise (lecture seule, déjà fixée) */}
                <div className="flex items-center gap-3 p-3 rounded-xl ring-1 ring-blue-100 bg-blue-50/50">
                  <Building2 className="w-4 h-4 text-blue-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500">Entreprise (associée automatiquement)</p>
                    <p className="font-semibold text-slate-800 truncate">
                      {selectedCompany ? decodeHtmlEntities(selectedCompany.name) : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Section Champs personnalisés */}
              {contactCustomFields.length > 0 && (
                <div className="border-t border-gray-200 pt-6 space-y-4">
                  <h3 className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    <SlidersHorizontal className="w-4 h-4" /> Champs personnalisés
                  </h3>
                  <div className="rounded-xl ring-1 ring-gray-200 divide-y divide-gray-100 overflow-hidden">
                    {contactCustomFields.map((field) => (
                      <div key={field.id} className="flex items-center justify-between gap-4 px-4 py-3">
                        <Label className="text-sm font-medium text-gray-700">
                          {field.name}
                          {field.required === 1 && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        <div className="shrink-0 w-48 flex justify-end">
                          {renderNewContactCustomField(field)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50">
              <Button
                variant="outline"
                onClick={() => setIsAddContactOpen(false)}
                disabled={isCreatingContact}
              >
                Annuler
              </Button>
              <Button
                onClick={submitAddContact}
                disabled={isCreatingContact}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isCreatingContact ? 'Création...' : 'Créer le contact'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation : suppression d'un contact associé depuis la fiche entreprise */}
      <Dialog open={!!contactToRemove} onOpenChange={(open) => { if (!open) setContactToRemove(null) }}>
        <DialogContent className={`${contactToRemove?.upspot_compte_id ? 'sm:max-w-[540px]' : 'sm:max-w-[425px]'} rounded-2xl sm:rounded-2xl`}>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              <span>Supprimer ce contact</span>
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer{' '}
              <span className="font-semibold text-gray-900">
                {contactToRemove?.first_name} {contactToRemove?.last_name}
              </span>{' '}
              du CRM ? Il ira dans la corbeille, d'où un administrateur pourra le restaurer.
            </DialogDescription>
          </DialogHeader>
          {contactToRemove?.upspot_compte_id && (
            <ChoixSuppressionUpSpot
              contactId={contactToRemove.id}
              choix={choixUpSpot}
              onChoix={setChoixUpSpot}
              confirme={suppressionConfirmee}
              onConfirme={setSuppressionConfirmee}
            />
          )}
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setContactToRemove(null)} disabled={isRemovingContact}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRemoveContact}
              disabled={
                isRemovingContact ||
                Boolean(
                  contactToRemove?.upspot_compte_id &&
                    !suppressionPossible(estAdminCrm, choixUpSpot, suppressionConfirmee),
                )
              }
              className="bg-red-600 hover:bg-red-700"
            >
              {isRemovingContact ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {fusion && (
        <FusionDialog
          type="entreprise"
          open
          ficheId={fusion.ficheId}
          autreId={fusion.autreId}
          onClose={() => setFusion(null)}
          onFusion={(gardeId) => {
            setFusion(null)
            void loadCompanies()
            void ouvrirEntreprise(gardeId)
          }}
        />
      )}

      {/* Modal de confirmation de suppression */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-[425px] rounded-2xl sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              <span>Confirmer la suppression</span>
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer cette entreprise ? Elle ira dans la corbeille, d'où un administrateur pourra la restaurer. Ses contacts restent, sans entreprise.
            </DialogDescription>
          </DialogHeader>
          
          {companyToDelete && (
            <div className="py-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium text-gray-900">
                  {companyToDelete.name}
                </p>
                {companyToDelete.website && (
                  <p className="text-sm text-gray-600">{companyToDelete.website}</p>
                )}
                {(companyToDelete.street_address || companyToDelete.city) && (
                  <p className="text-sm text-gray-600">
                    {companyToDelete.street_address}
                    {companyToDelete.street_address && companyToDelete.city && ', '}
                    {companyToDelete.city}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={cancelDeleteCompany}
              disabled={isDeleting}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteCompany}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Mettre à la corbeille
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}